// Language translations for the weather app

export type Language = 'sv' | 'en' | 'da' | 'no' | 'fi';

export interface Translations {
  // Header
  appTitle: string;
  serviceFrom: string;
  contactUs: string;
  
  // Share
  shareWeather: string;
  linkCopied: string;
  weatherAppTitle: string;
  checkWeatherFor: string;
  
  // Welcome Screen
  welcomeTitle: string;
  welcomeSubtitle: string;
  welcomeButton: string;
  
  // Map & Controls
  selectLocation: string;
  clickToExplore: string;
  currentLocationBtn: string;
  locationError: string;
  
  // Date
  selectDate: string;
  time: string;
  
  // Weather Display
  loading: string;
  weather: string;
  noWeatherData: string;
  temperature: string;
  feelsLike: string;
  humidity: string;
  pressure: string;
  wind: string;
  windGust: string;
  precipitation: string;
  visibility: string;
  cloudiness: string;
  sunrise: string;
  sunset: string;
  moonPhase: string;
  sunAndMoon: string;
  
  // Weather Conditions
  weatherClear: string;
  weatherMostlyClear: string;
  weatherPartlyCloudy: string;
  weatherCloudy: string;
  weatherFog: string;
  weatherRimeFog: string;
  weatherLightDrizzle: string;
  weatherModerateDrizzle: string;
  weatherDenseDrizzle: string;
  weatherLightRain: string;
  weatherModerateRain: string;
  weatherHeavyRain: string;
  weatherLightSnow: string;
  weatherModerateSnow: string;
  weatherHeavySnow: string;
  weatherLightShowers: string;
  weatherModerateShowers: string;
  weatherHeavyShowers: string;
  weatherThunderstorm: string;
  weatherThunderstormHail: string;
  weatherUnknown: string;
  historicalWeather: string;
  currentWeather: string;
  forecast: string;
  
  // Moon Phases
  newMoon: string;
  waxingCrescent: string;
  firstQuarter: string;
  waxingGibbous: string;
  fullMoon: string;
  waningGibbous: string;
  lastQuarter: string;
  waningCrescent: string;
  
  // Hourly Weather
  hourlyForecast: string;
  hourlyForecastShort: string;
  hour: string;
  
  // Weather History
  weatherHistory: string;
  daysBackAndForward: string;
  daysBackAndForward2: string;
  historicalWeatherDevelopment: string;
  astronomy: string;
  maxTemp: string;
  minTemp: string;
  windSpeed: string;
  windDirection: string;
  windGustLabel: string;
  timeHours: string;
  moonPhasePercent: string;
  
  // Weather Trends - Insights
  tempChange: string;
  pressureChange: string;
  minPressure: string;
  maxPressure: string;
  avgWind: string;
  maxGust: string;
  dominantDirection: string;
  windSpan: string;
  totalPrecipitation: string;
  maxPerDay: string;
  rainyDays: string;
  avgScore: string;
  maxScore: string;
  quality: string;
  catchDay: string;
  catchMomentLegend: string;
  
  // Fishing Forecast
  fishingForecast: string;
  advancedAlgorithm: string;
  fishingScore: string;
  today: string;
  excellentFishing: string;
  goodConditions: string;
  moderateConditions: string;
  difficultConditions: string;
  positiveFactors: string;
  negativeFactors: string;
  hourByHour: string;
  upcomingDays: string;
  species: string;
  
  // Fishing factors
  excellentLight: string;
  goodLight: string;
  moderateLight: string;
  poorLight: string;
  stableHighPressure: string;
  strongWeatherFront: string;
  strongPressureFront: string;
  pressureFront: string;
  niceWind: string;
  extremeConditions: string;
  tooHard: string;
  dangerousGusts: string;
  poorWindConditions: string;
  stronglyGusty: string;
  hardGusts: string;
  problematicWind: string;
  excellentWeather: string;
  poorWeatherFront: string;
  poorWeatherWind: string;
  poorWeatherRain: string;
  poorWeather: string;
  acceptableWeather: string;
  goodWaterTemp: string;
  unfavorableWaterTemp: string;
  moderateWaterTemp: string;
  weatherFrontAffects: string;
  veryStableWeather: string;
  unstableWeather: string;
  extremelyWindy: string;
  veryWindy: string;
  hardWindy: string;
  dangerousGustsWarning: string;
  extremeRain: string;
  veryHeavyRain: string;
  extremelyPoorVisibility: string;
  veryPoorVisibility: string;
  
  // Species names
  pike: string;
  perch: string;
  pikeperch: string; // zander
  zander: string; // deprecated, use pikeperch
  general: string;
  trout: string;
  salmon: string;
  seaTrout: string;
  roach: string;
  bream: string;
  carp: string;
  tench: string;
  ruffe: string;
  crucian: string;
  other: string;
  
  // AI Recommendation
  alfredAI: string;
  aiAnalyzing: string;
  aiWaitTime: string;
  aiCacheInfo: string;
  aiWelcomeTitle: string;
  aiWelcomeIntro: string;
  aiWelcomePastDate: string;
  aiWelcomeHistorical: string;
  aiStartQuestions: string;
  aiQuestionIntro: string;
  aiStartChat: string;
  aiFishingFromShore: string;
  aiFishingMethod: string;
  aiWaterClarity: string;
  aiYes: string;
  aiNo: string;
  aiMethodSpin: string;
  aiMethodBait: string;
  aiMethodTrolling: string;
  aiMethodFly: string;
  aiMethodVertical: string;
  aiMethodLivescope: string;
  aiClarityClear: string;
  aiClarityModerate: string;
  aiClarityMurky: string;
  aiSendMessage: string;
  aiTypingPlaceholder: string;
  aiWaitingMessage: string;
  aiRateLimitTitle: string;
  aiRateLimitMessage: string;
  aiRateLimitResetAt: string;
  aiErrorTitle: string;
  aiTooManyRequestsTitle: string;
  aiTooManyRequestsMessage: string;
  minute: string;
  minutes: string;
  
  // Wind Simulator
  windSimulator: string;
  hours: string;
  days: string;
  play: string;
  pause: string;
  reset: string;
  speed: string;
  
  // Wind strength
  calm: string;
  light: string;
  moderate: string;
  strong: string;
  gale: string;
  
  // Wind directions
  north: string;
  northeast: string;
  east: string;
  southeast: string;
  south: string;
  southwest: string;
  west: string;
  northwest: string;
  
  // Favorites
  favorites: string;
  addToFavorites: string;
  removeFromFavorites: string;
  noFavorites: string;
  
  // Tutorial
  tutorialSkip: string;
  tutorialNext: string;
  tutorialComplete: string;
  tutorialMapTitle: string;
  tutorialMapDesc: string;
  tutorialDateTitle: string;
  tutorialDateDesc: string;
  tutorialLocationTitle: string;
  tutorialLocationDesc: string;
  tutorialWeatherTitle: string;
  tutorialWeatherDesc: string;
  tutorialHourlyTitle: string;
  tutorialHourlyDesc: string;
  tutorialHistoryTitle: string;
  tutorialHistoryDesc: string;
  tutorialFishingTitle: string;
  tutorialFishingDesc: string;
  tutorialAITitle: string;
  tutorialAIDesc: string;
  tutorialWindTitle: string;
  tutorialWindDesc: string;
  tutorialFavoritesTitle: string;
  tutorialFavoritesDesc: string;
  tutorialNavigationTitle: string;
  tutorialNavigationDesc: string;
  
  // QuickCatchModal
  quickCatchTitle: string;
  speciesLabel: string;
  typeLabel: string;
  typeCaught: string;
  typeContact: string;
  countLabel: string;
  moreDetails: string;
  weightLabel: string;
  lengthLabel: string;
  waterTempLabel: string;
  depthLabel: string;
  depthPlaceholder: string;
  photoLabel: string;
  takePhoto: string;
  chooseFromGallery: string;
  notesLabel: string;
  notesPlaceholder: string;
  advancedBacklog: string;
  dateLabel: string;
  datePlaceholder: string;
  timeLabel: string;
  timePlaceholder: string;
  sessionLabel: string;
  autoCreateSession: string;
  noSession: string;
  activeSessions: string;
  completedSessions: string;
  areaLabel: string;
  selectArea: string;
  customLocation: string;
  activePassSeparator: string;
  completedPassSeparator: string;
  locationLabel: string;
  chooseOnMap: string;
  saveLocation: string;
  cancelLocation: string;
  logCatch: string;
  logCatches: string;
  catchLogged: string;
  catchesLogged: string;
  catchLogFailed: string;
  cannotLogFutureDate: string;
  photoCaptured: string;
  photoCaptureFailed: string;
  photoUploaded: string;
  photoUploadFailed: string;
  photoRemoved: string;
  selectSpecies: string;
  noArea: string;
  useGPS: string;
  useGPSDescription: string;
  customLocationDescription: string;
  noAreaDescription: string;
  sessionAutoCreateDescription: string;
  sessionNoSessionDescription: string;
  sessionActiveDescription: string;
  
  // Toast messages
  locationAdded: string;
  locationRemoved: string;
  errorOccurred: string;
  errorFetchingWeather: string;
  
  // Settings
  settings: string;
  language: string;
  units: string;
  defaultFishSpecies: string;
  metric: string;
  imperial: string;
  saveSettings: string;
  settingsUpdated: string;
  exportBackup: string;
  exportImportData: string;
  offlineDataSync: string;
  
  // Footer
  weatherDataFrom: string;
  mapsFrom: string;
  builtBy: string;
  newHere: string;
  showGuide: string;
  
  // Navigation Menu
  navigationWeather: string;
  catches: string;
  sessions: string;
  areas: string;
  
  // Catches Page
  noCatchesYet: string;
  noCatchesDescription: string;
  totalCatches: string;
  landed: string;
  released: string;
  photos: string;
  loadingCatches: string;
  unknownLocation: string;
  
  // Catch Detail Page
  backToCatches: string;
  measurements: string;
  waterConditions: string;
  fishingDepth: string;
  outOf100: string;
  quantity: string;
  catch: string;
  notRecorded: string;
  end: string;
  
  // Sessions Page
  noSessionsYet: string;
  noSessionsDescription: string;
  sessionsTotal: string;
  active: string;
  completed: string;
  fishingSession: string;
  start: string;
  duration: string;
  waterTemp: string;
  loadingSessions: string;
  
  // Areas Page
  noAreasYet: string;
  noAreasDescription: string;
  areasVisited: string;
  place: string;
  places: string;
  coordinates: string;
  radius: string;
  lastVisit: string;
  loadingAreas: string;
  
  // Catch Detail Page
  sunriseLabel: string;
  sunsetLabel: string;
  topCatches: string;
  excellent: string;
  good: string;
  fair: string;
  keepTrying: string;
  
  // Fishing Methods
  fishingMethod: string;
  tackle: string;
  methodSpin: string;
  methodJigging: string;
  methodTrolling: string;
  methodFly: string;
  methodBait: string;
  methodVertical: string;
  methodLivescope: string;
  methodIce: string;
  
  // Tackle & Bait
  tackleWobbler: string;
  tackleSoftbait: string;
  tackleSpoon: string;
  tackleJig: string;
  tackleCrankbait: string;
  tackleSpinnerbait: string;
  tackleTopwater: string;
  tackleSwimbait: string;
  tackleDryfly: string;
  tackleNymph: string;
  tackleStreamer: string;
  tackleLivebait: string;
  tackleDeadbait: string;
  tackleWorm: string;
  tackleMaggot: string;
  tackleCorn: string;
  tackleDough: string;
  tackleDropshot: string;
  tackleCarolina: string;
  tackleTexas: string;
  
  // Fishing Details
  selectMethod: string;
  selectTackle: string;
  recommendedMethods: string;
  recentlyUsed: string;
  customTackle: string;
  addCustomTackle: string;
  addMethod: string;
  manageMethods: string;
  manageTackle: string;
  manageSpecies: string;
  addSpecies: string;
  added: string;
  removed: string;
  error: string;
  defaultLabel: string;
  
  // Confirmation dialogs
  confirmRemove: string;
  cancel: string;
  removeSpeciesWarning: string;
  removeMethodWarning: string;
  removeTackleWarning: string;
  catchesWillRemain: string;
  
  // Default preferences
  defaultSpecies: string;
  defaultSpeciesHelp: string;
  defaultMethod: string;
  defaultMethodHelp: string;
  defaultTackle: string;
  defaultTackleHelp: string;
  clickToSetDefault: string;
  clickToSetDefaultMultiple: string;
  
  // CatchesPage Redesign - Filter & Search
  search: string;
  clearSearch: string;
  searchCatches: string;
  filters: string;
  clearFilters: string;
  applyFilters: string;
  allDates: string;
  thisWeek: string;
  thisMonth: string;
  thisYear: string;
  allAreas: string;
  
  // CatchesPage Redesign - Saved Filters
  allCatches: string;
  createNewFilter: string;
  configure: string;
  saveFilter: string;
  updateFilter: string;
  deleteFilter: string;
  filterName: string;
  filterIcon: string;
  noSavedFilters: string;
  
  // CatchesPage Redesign - Advanced Filters
  methods: string;
  baits: string;
  seasons: string;
  spring: string;
  summer: string;
  fall: string;
  winter: string;
  timeOfDay: string;
  morning: string;
  afternoon: string;
  evening: string;
  night: string;
  weatherFilters: string;
  windDirections: string;
  
  // CatchesPage Redesign - Sort & Group
  groupBy: string;
  sortBy: string;
  date: string;
  area: string;
  session: string;
  none: string;
  newest: string;
  oldest: string;
  weight: string;
  length: string;
  
  // CatchesPage Redesign - View modes
  view: string;
  list: string;
  grid: string;
  map: string;
  
  // CatchesPage Redesign - Selection & Bulk
  selectMultiple: string;
  selected: string;
  delete: string;
  export: string;
  changeArea: string;
  addToSession: string;
  
  // CatchesPage Redesign - Stats
  contact: string;
  total: string;
  
  // CatchesPage Redesign - Map
  showMarkers: string;
  showHeatmap: string;
  viewDetails: string;
  
  // CatchesPage Redesign - Weather Similarity
  findSimilarWeather: string;
  similarConditions: string;
  referenceWeather: string;
  foundSimilar: string;
  matchScore: string;
  viewAllOnMap: string;
  exportSimilar: string;
  
  // CatchesPage Redesign - Empty states
  noMatchingCatches: string;
  clearFiltersToSeeMore: string;
  
  // CatchesPage Redesign - Loading
  loadMore: string;
}

export const translations: Record<Language, Translations> = {
  sv: {
    // Header
    appTitle: 'Fiske & Väder',
    serviceFrom: 'En tjänst från skitfiske.app',
    contactUs: 'Kontakta oss',
    
    // Share
    shareWeather: 'Dela väder',
    linkCopied: 'Länk kopierad till urklipp!',
    weatherAppTitle: 'VäderApp - Väderprognos',
    checkWeatherFor: 'Kolla in vädret för',
    
    // Welcome Screen
    welcomeTitle: 'Välkommen till Fiske & Väder',
    welcomeSubtitle: 'Din smarta fiskeguide med AI-analys',
    welcomeButton: 'Kom igång',
    
    // Map & Controls
    selectLocation: 'Välj plats på kartan',
    clickToExplore: 'Utforska',
    currentLocationBtn: 'Min position',
    locationError: 'Kunde inte hämta position',
    
    // Date
    selectDate: 'Välj datum',
    time: 'Tid',
    
    // Weather Display
    loading: 'Laddar...',
    weather: 'Väder',
    noWeatherData: 'Ingen väderdata tillgänglig',
    temperature: 'Temperatur',
    feelsLike: 'Känns som',
    humidity: 'Luftfuktighet',
    pressure: 'Lufttryck',
    wind: 'Vind',
    windGust: 'Byar',
    precipitation: 'Nederbörd',
    visibility: 'Sikt',
    cloudiness: 'Molnighet',
    sunrise: 'Soluppgång',
    sunset: 'Solnedgång',
    moonPhase: 'Månfas',
    sunAndMoon: 'Sol & Måne',
    
    // Weather Conditions
    weatherClear: 'Klart',
    weatherMostlyClear: 'Mest klart',
    weatherPartlyCloudy: 'Delvis molnigt',
    weatherCloudy: 'Molnigt',
    weatherFog: 'Dimma',
    weatherRimeFog: 'Rimfrost dimma',
    weatherLightDrizzle: 'Lätt duggregn',
    weatherModerateDrizzle: 'Måttligt duggregn',
    weatherDenseDrizzle: 'Tätt duggregn',
    weatherLightRain: 'Lätt regn',
    weatherModerateRain: 'Måttligt regn',
    weatherHeavyRain: 'Kraftigt regn',
    weatherLightSnow: 'Lätt snöfall',
    weatherModerateSnow: 'Måttligt snöfall',
    weatherHeavySnow: 'Kraftigt snöfall',
    weatherLightShowers: 'Lätta regnskurar',
    weatherModerateShowers: 'Måttliga regnskurar',
    weatherHeavyShowers: 'Kraftiga regnskurar',
    weatherThunderstorm: 'Åska',
    weatherThunderstormHail: 'Åska med hagel',
    weatherUnknown: 'Okänt',
    historicalWeather: 'Historiskt väder',
    currentWeather: 'Aktuellt väder',
    forecast: 'Prognos',
    
    // Moon Phases
    newMoon: 'Nymåne',
    waxingCrescent: 'Tilltagande skära',
    firstQuarter: 'Första kvarteret',
    waxingGibbous: 'Tilltagande halvmåne',
    fullMoon: 'Fullmåne',
    waningGibbous: 'Avtagande halvmåne',
    lastQuarter: 'Sista kvarteret',
    waningCrescent: 'Avtagande skära',
    
    // Hourly Weather
    hourlyForecast: 'Timprognos',
    hourlyForecastShort: '24h prognos',
    hour: 'Timme',
    
    // Weather History
    weatherHistory: 'Väderhistorik',
    daysBackAndForward: '5 dagar bakåt & framåt från',
    daysBackAndForward2: '5 dagar bakåt & framåt från {date}',
    historicalWeatherDevelopment: 'Historisk väderutveckling för vald plats',
    astronomy: 'Astronomi',
    maxTemp: 'Max temp',
    minTemp: 'Min temp',
    windSpeed: 'Vindhastighet',
    windDirection: 'Vindriktning',
    windGustLabel: 'Vindbyar',
    timeHours: 'Tid (timmar)',
    moonPhasePercent: 'Månfas (%)',
    
    // Weather Trends - Insights
    tempChange: 'Temp förändring',
    pressureChange: 'Tryck förändring',
    minPressure: 'Min tryck',
    maxPressure: 'Max tryck',
    avgWind: 'Snitt vind',
    maxGust: 'Max by',
    dominantDirection: 'Dominerande riktning',
    windSpan: 'Vind span',
    totalPrecipitation: 'Total nederbörd',
    maxPerDay: 'Max/dag',
    rainyDays: 'Regndagar',
    avgScore: 'Snitt score',
    maxScore: 'Max score',
    quality: 'Kvalitet',
    catchDay: 'FÅNGSTDAG',
    catchMomentLegend: 'Grön punkt = Fångsttillfälle',
    
    // Fishing Forecast
    fishingForecast: 'Fiskeprognos',
    advancedAlgorithm: 'Avancerad algoritm baserat på väderdata & månfaser',
    fishingScore: 'Fiskescore',
    today: 'idag',
    excellentFishing: 'Utmärkt fiskeväder!',
    goodConditions: 'Bra förhållanden',
    moderateConditions: 'Måttliga förhållanden',
    difficultConditions: 'Svåra förhållanden',
    positiveFactors: 'Positiva:',
    negativeFactors: 'Negativa:',
    hourByHour: 'Timme för timme',
    upcomingDays: '5 dagar bakåt & framåt från {date}',
    species: 'Art:',
    
    // Fishing factors
    excellentLight: 'Utmärkt ljusförhållanden',
    goodLight: 'Bra ljus för fiske',
    moderateLight: 'Måttliga ljusförhållanden',
    poorLight: 'Dåliga ljusförhållanden',
    stableHighPressure: 'Stabilt högtryck gynnar',
    strongWeatherFront: 'Kraftig väderfront!',
    strongPressureFront: 'Stark tryckfront',
    pressureFront: 'Tryckfront',
    niceWind: 'Fin vind',
    extremeConditions: 'Extrema förhållanden!',
    tooHard: 'För hårt!',
    dangerousGusts: 'Farligt byigt!',
    poorWindConditions: 'Dåliga vindförhållanden',
    stronglyGusty: 'Kraftigt byigt',
    hardGusts: 'Hårda byar',
    problematicWind: 'Problematisk vind',
    excellentWeather: 'Utmärkta väderförhållanden',
    poorWeatherFront: 'Dåligt väder: tryckfront & hård vind',
    poorWeatherWind: 'Dåligt väder: för hård vind',
    poorWeatherRain: 'Dåligt väder: regn/dålig sikt',
    poorWeather: 'Dåliga väderförhållanden',
    acceptableWeather: 'Acceptabla väderförhållanden',
    goodWaterTemp: 'Bra vattentemp',
    unfavorableWaterTemp: 'Ogynnsam vattentemp',
    moderateWaterTemp: 'Måttlig vattentemp',
    weatherFrontAffects: 'Väderfront påverkar',
    veryStableWeather: 'Mycket stabil väder',
    unstableWeather: 'Instabilt väder',
    extremelyWindy: 'Extremt blåsigt - FARLIGT',
    veryWindy: 'Mycket blåsigt',
    hardWindy: 'Hårt blåsigt',
    dangerousGustsWarning: 'Farliga byar',
    extremeRain: 'Extremt kraftigt regn',
    veryHeavyRain: 'Mycket kraftigt regn',
    extremelyPoorVisibility: 'Extremt dålig sikt - FARLIGT',
    veryPoorVisibility: 'Mycket dålig sikt',
    
    // Species names
    pike: 'Gädda',
    perch: 'Abborre',
    pikeperch: 'Gös',
    zander: 'Gös', // deprecated
    general: 'Generell',
    trout: 'Öring',
    salmon: 'Lax',
    seaTrout: 'Havsöring',
    roach: 'Mört',
    bream: 'Braxen',
    carp: 'Karp',
    tench: 'Sutare',
    ruffe: 'Gers',
    crucian: 'Ruda',
    other: 'Annat',
    
    // AI Recommendation
    alfredAI: 'Alfred AI Fiskeguide',
    aiAnalyzing: 'Alfred analyserar...',
    aiWaitTime: 'Detta kan ta upp till 30 sekunder ⏱️',
    aiCacheInfo: 'Konversation sparad per plats, datum, språk & art • Inkl. väderhistorik & månfas',
    aiWelcomeTitle: 'Hej, mitt namn är Alfred! 👋',
    aiWelcomeIntro: 'Jag kan hjälpa dig planera din fiskedag för',
    aiWelcomePastDate: 'Vill du hellre planera dagens fisketur eller ett annat datum i framtiden?',
    aiWelcomeHistorical: 'Jag använder historisk väderdata och prognoser för att ge dig bästa möjliga råd.',
    aiStartQuestions: 'Börja planera',
    aiQuestionIntro: 'Berätta lite om ditt fiske så kan Alfred ge bättre råd:',
    aiStartChat: 'Starta Alfred',
    aiFishingFromShore: 'Fiskar du från land?',
    aiFishingMethod: 'Vilken fiskemetod?',
    aiWaterClarity: 'Vattensikt?',
    aiYes: 'Ja',
    aiNo: 'Nej',
    aiMethodSpin: 'Spinn',
    aiMethodBait: 'Mete',
    aiMethodTrolling: 'Trolling',
    aiMethodFly: 'Fluga',
    aiMethodVertical: 'Vertikal',
    aiMethodLivescope: 'Livescope',
    aiClarityClear: 'Klar',
    aiClarityModerate: 'Måttlig',
    aiClarityMurky: 'Grumlig',
    aiSendMessage: 'Skicka',
    aiTypingPlaceholder: 'Skriv ett meddelande till Alfred...',
    aiWaitingMessage: 'Hej, jag tänker en stund...',
    aiRateLimitTitle: 'Gräns nådd',
    aiRateLimitMessage: 'Du har nått max antal AI-förfrågningar (10 per timme). Försök igen om {minutes} minut{plural}.',
    aiRateLimitResetAt: 'Återställs kl:',
    aiErrorTitle: 'Fel',
    aiTooManyRequestsTitle: 'För många förfrågningar',
    aiTooManyRequestsMessage: 'Vänta ett ögonblick innan du gör en ny förfrågan.',
    minute: 'minut',
    minutes: 'minuter',
    
    // Wind Simulator
    windSimulator: 'Vindsimulator',
    hours: 'timmar',
    days: 'dagar',
    play: 'Spela',
    pause: 'Pausa',
    reset: 'Återställ till början',
    speed: 'Hastighet:',
    
    // Wind strength
    calm: 'Lugn',
    light: 'Lätt',
    moderate: 'Måttlig',
    strong: 'Hård',
    gale: 'Storm',
    
    // Wind directions
    north: 'N',
    northeast: 'NO',
    east: 'O',
    southeast: 'SO',
    south: 'S',
    southwest: 'SV',
    west: 'V',
    northwest: 'NV',
    
    // Favorites
    favorites: 'Favoriter',
    addToFavorites: 'Lägg till favorit',
    removeFromFavorites: 'Ta bort favorit',
    noFavorites: 'Inga favoriter sparade ännu',
    
    // Tutorial
    tutorialSkip: 'Hoppa över',
    tutorialNext: 'Nästa',
    tutorialComplete: 'Slutför',
    tutorialMapTitle: '🗺️ Interaktiv Karta',
    tutorialMapDesc: 'Klicka var som helst på kartan för att välja en plats och se väderdata.',
    tutorialDateTitle: '📅 Välj Datum',
    tutorialDateDesc: 'Välj ett datum för att se historiskt väder eller prognos.',
    tutorialLocationTitle: '📍 Min Position',
    tutorialLocationDesc: 'Tryck här för att automatiskt hitta din nuvarande position.',
    tutorialWeatherTitle: '🌤️ Väderinformation',
    tutorialWeatherDesc: 'Se detaljerad väderinformation för vald plats och datum.',
    tutorialHourlyTitle: '⏰ Timprognos',
    tutorialHourlyDesc: 'Utforska väderprognosen timme för timme.',
    tutorialHistoryTitle: '📈 Väderhistorik',
    tutorialHistoryDesc: 'Se väderutvecklingen 5 dagar bakåt och 5 dagar framåt.',
    tutorialFishingTitle: '🎣 Fiskeprognos',
    tutorialFishingDesc: 'AI-driven fiskeprognos baserad på väderdata. Välj fiskart för skräddarsydda rekommendationer.',
    tutorialAITitle: '🤖 Alfred Fiske-AI',
    tutorialAIDesc: 'Få personliga AI-rekommendationer från Alfred baserat på alla väderförhållanden. Perfekt för både nybörjare och erfarna fiskare!',
    tutorialWindTitle: '🌬️ Vindsimulator',
    tutorialWindDesc: 'Se animerade vindpilar som visar vindriktning och styrka timme för timme. Spela upp animationen för att se hur vinden förändras över tid.',
    tutorialFavoritesTitle: '⭐ Favoriter',
    tutorialFavoritesDesc: 'Spara dina favoritplatser för snabb åtkomst.',
    tutorialNavigationTitle: '🧭 Navigation',
    tutorialNavigationDesc: 'Navigera mellan fiske-sidor, visa dina fångster, pass, platser och statistik.',
    
    // QuickCatchModal
    quickCatchTitle: '⚡ Snabblogg Fångst',
    speciesLabel: '🐟 Art',
    typeLabel: '🎯 Typ',
    typeCaught: '🎣 Landad',
    typeContact: '💨 Kontakt',
    countLabel: '🔢 Antal',
    moreDetails: 'Mer detaljer',
    weightLabel: '⚖️ Vikt',
    lengthLabel: '📏 Längd',
    waterTempLabel: '🌡️ Vattentemperatur (°C)',
    depthLabel: '📐 Fiskedjup',
    depthPlaceholder: 'Djup',
    photoLabel: '📸 Foto',
    takePhoto: '📷 Ta foto',
    chooseFromGallery: '🖼️ Välj från galleri',
    notesLabel: '📝 Anteckningar',
    notesPlaceholder: 'Valfria anteckningar...',
    advancedBacklog: '⚙️ Avancerat (backlog)',
    dateLabel: '📅 Datum',
    datePlaceholder: 'Lämna tomt för dagens datum',
    timeLabel: '⏰ Tid',
    timePlaceholder: 'Lämna tomt för aktuell tid',
    sessionLabel: '🎯 Fiskepass',
    autoCreateSession: 'Auto-skapa nytt pass',
    noSession: 'Inget pass',
    activeSessions: '──── Aktiva Pass ────',
    completedSessions: '──── Avslutade Pass ────',
    areaLabel: '📍 Område',
    selectArea: 'Välj område',
    customLocation: 'Egen plats',
    activePassSeparator: 'Aktiva Pass',
    completedPassSeparator: 'Avslutade Pass',
    locationLabel: '🗺️ Plats',
    chooseOnMap: '📍 Välj på karta',
    saveLocation: '💾 Spara plats',
    cancelLocation: '✕ Avbryt',
    logCatch: '✅ Logga fångst',
    logCatches: '✅ Logga fångster',
    catchLogged: 'loggad',
    catchesLogged: 'loggade',
    catchLogFailed: '❌ Kunde inte logga fångst',
    cannotLogFutureDate: '❌ Kan inte logga fångst i framtiden',
    photoCaptured: '📷 Foto taget!',
    photoCaptureFailed: '❌ Kunde inte ta foto',
    photoUploaded: '🖼️ Foto uppladdat!',
    photoUploadFailed: '❌ Kunde inte ladda upp foto',
    photoRemoved: '🗑️ Foto borttaget',
    selectSpecies: 'Välj en art först',
    noArea: 'Ingen plats',
    useGPS: 'Använd GPS-position',
    useGPSDescription: 'Hämtar din nuvarande position',
    customLocationDescription: 'Välj plats på karta',
    noAreaDescription: 'Sparar fångst utan platsinfo',
    sessionAutoCreateDescription: 'Skapar nytt pass för denna fångst',
    sessionNoSessionDescription: 'Ingen koppling till pass',
    sessionActiveDescription: '🟢 = Pågående pass, ⚪ = Avslutade pass',
    
    // Toast messages
    locationAdded: 'Plats tillagd i favoriter',
    locationRemoved: 'Plats borttagen från favoriter',
    errorOccurred: 'Ett fel uppstod',
    errorFetchingWeather: 'Kunde inte hämta väderdata',
    
    // Settings
    settings: 'Inställningar',
    language: 'Språk',
    units: 'Enheter',
    defaultFishSpecies: 'Förvald fiskart',
    metric: 'Metriskt',
    imperial: 'Imperial',
    saveSettings: 'Spara inställningar',
    settingsUpdated: 'Inställningar uppdaterade',
    exportBackup: 'Export & Backup',
    exportImportData: 'Exportera / Importera Data',
    offlineDataSync: 'Väderdata och platsnamn synkas automatiskt när du går online igen!',
    
    // Footer
    weatherDataFrom: 'Väderdata från',
    mapsFrom: 'Kartor från',
    builtBy: 'Byggd av',
    newHere: 'Ny här?',
    showGuide: 'Visa guide',
    
    // Navigation Menu
    navigationWeather: 'Väder',
    catches: 'Fångster',
    sessions: 'Fiskepass',
    areas: 'Platser',
    
    // Catches Page
    noCatchesYet: 'Inga fångster än',
    noCatchesDescription: 'Tryck på +-knappen för att logga din första fångst!',
    totalCatches: 'totalt',
    landed: 'landade',
    released: 'släppta',
    photos: 'foton',
    loadingCatches: 'Laddar fångster...',
    unknownLocation: 'Okänd plats',
    
    // Catch Detail Page
    backToCatches: 'Tillbaka till fångster',
    measurements: 'Mått',
    waterConditions: 'Vattenförhållanden',
    fishingDepth: 'Fiskedjup',
    outOf100: 'av 100',
    quantity: 'Antal',
    catch: 'fångst',
    notRecorded: 'Ej registrerad',
    end: 'Slut',
    
    // Sessions Page
    noSessionsYet: 'Inga fiskepass än',
    noSessionsDescription: 'Starta ett pass genom att logga en fångst!',
    sessionsTotal: 'pass totalt',
    active: 'aktiva',
    completed: 'Avslutad',
    fishingSession: 'Fiskepass',
    start: 'Start',
    duration: 'Varaktighet',
    waterTemp: 'Vattentemp',
    loadingSessions: 'Laddar fiskepass...',
    
    // Areas Page
    noAreasYet: 'Inga platser än',
    noAreasDescription: 'Fiskeplatser skapas automatiskt när du loggar fångster!',
    areasVisited: 'besökta',
    place: 'plats',
    places: 'platser',
    coordinates: 'Koordinater',
    radius: 'Radie',
    lastVisit: 'Senaste besöket',
    loadingAreas: 'Laddar områden...',
    
    // Catch Detail Page
    sunriseLabel: 'Soluppgång',
    sunsetLabel: 'Solnedgång',
    topCatches: 'Top 5',
    excellent: 'Utmärkt',
    good: 'Bra',
    fair: 'Acceptabelt',
    keepTrying: 'Fortsätt försöka',
    
    // Fishing Methods
    fishingMethod: 'Fiskemetod',
    methodSpin: 'Spinn',
    methodJigging: 'Jigg',
    methodTrolling: 'Trolling',
    methodFly: 'Flugfiske',
    methodBait: 'Betesfiske',
    methodVertical: 'Vertikalt',
    methodLivescope: 'Livescope',
    methodIce: 'Isfiske',
    
    // Tackle & Bait
    tackle: 'Beten & Utrustning',
    tackleWobbler: 'Wobbler',
    tackleSoftbait: 'Gummibete',
    tackleSpoon: 'Spinnare',
    tackleJig: 'Jig',
    tackleCrankbait: 'Crankbait',
    tackleSpinnerbait: 'Spinnerbait',
    tackleTopwater: 'Topwater',
    tackleSwimbait: 'Swimbait',
    tackleDryfly: 'Torrfluga',
    tackleNymph: 'Nymf',
    tackleStreamer: 'Streamer',
    tackleLivebait: 'Levande bete',
    tackleDeadbait: 'Dött bete',
    tackleWorm: 'Mask',
    tackleMaggot: 'Maggot',
    tackleCorn: 'Majs',
    tackleDough: 'Deg',
    tackleDropshot: 'Dropshot-rigg',
    tackleCarolina: 'Carolina-rigg',
    tackleTexas: 'Texas-rigg',
    
    // Fishing Details UI
    selectMethod: 'Välj metod',
    selectTackle: 'Välj beten',
    recommendedMethods: 'Rekommenderat',
    recentlyUsed: 'Senast använda',
    customTackle: 'Anpassade beten',
    addCustomTackle: 'Lägg till eget bete',
    addMethod: 'Lägg till metod',
    manageMethods: 'Hantera fiskemetoder',
    manageTackle: 'Hantera beten',
    manageSpecies: 'Hantera arter',
    addSpecies: 'Lägg till art',
    added: 'tillagd',
    removed: 'borttagen',
    error: 'Fel',
    defaultLabel: 'standard',
    
    // Confirmation dialogs
    confirmRemove: 'Bekräfta borttagning',
    cancel: 'Avbryt',
    removeSpeciesWarning: 'Är du säker på att du vill ta bort denna art? Fångster med denna art kommer att behållas men kan få fel fiskepoäng och AI-rekommendationer.',
    removeMethodWarning: 'Är du säker på att du vill ta bort denna metod? Fångster med denna metod kommer att behållas.',
    removeTackleWarning: 'Är du säker på att du vill ta bort detta bete? Fångster med detta bete kommer att behållas.',
    catchesWillRemain: 'OBS: Befintliga fångster påverkas inte.',
    
    // Default preferences
    defaultSpecies: 'Standard art',
    defaultSpeciesHelp: 'Används för fiske-score på väder-sidan och i Alfred AI när du loggar fångst',
    defaultMethod: 'Standard metod',
    defaultMethodHelp: 'Väljs automatiskt i Alfred AI',
    defaultTackle: 'Standard beten',
    defaultTackleHelp: 'Väljs automatiskt i Alfred AI (kan välja flera)',
    clickToSetDefault: 'Klicka på en för att sätta som standard',
    clickToSetDefaultMultiple: 'Klicka för att sätta som standard (kan välja flera)',
    
    // CatchesPage Redesign - Filter & Search
    search: 'Sök',
    clearSearch: 'Rensa sökning',
    searchCatches: 'Sök fångster...',
    filters: 'Filter',
    clearFilters: 'Rensa filter',
    applyFilters: 'Visa resultat',
    allDates: 'Alla datum',
    thisWeek: 'Denna vecka',
    thisMonth: 'Denna månad',
    thisYear: 'Detta år',
    allAreas: 'Alla platser',
    
    // CatchesPage Redesign - Saved Filters
    allCatches: 'Alla fångster',
    createNewFilter: 'Skapa nytt filter',
    configure: 'Konfigurera',
    saveFilter: 'Spara filter',
    updateFilter: 'Uppdatera filter',
    deleteFilter: 'Radera filter',
    filterName: 'Filternamn',
    filterIcon: 'Ikon',
    noSavedFilters: 'Inga sparade filter',
    
    // CatchesPage Redesign - Advanced Filters
    methods: 'Metoder',
    baits: 'Beten',
    seasons: 'Årstider',
    spring: 'Vår',
    summer: 'Sommar',
    fall: 'Höst',
    winter: 'Vinter',
    timeOfDay: 'Tid på dagen',
    morning: 'Morgon',
    afternoon: 'Eftermiddag',
    evening: 'Kväll',
    night: 'Natt',
    weatherFilters: 'Väderfilter',
    windDirections: 'Vindriktningar',
    
    // CatchesPage Redesign - Sort & Group
    groupBy: 'Gruppera efter',
    sortBy: 'Sortera på',
    date: 'Datum',
    area: 'Plats',
    session: 'Pass',
    none: 'Ingen',
    newest: 'Nyast',
    oldest: 'Äldst',
    weight: 'Vikt',
    length: 'Längd',
    
    // CatchesPage Redesign - View modes
    view: 'Vy',
    list: 'Lista',
    grid: 'Rutnät',
    map: 'Karta',
    
    // CatchesPage Redesign - Selection & Bulk
    selectMultiple: 'Välj flera',
    selected: 'valda',
    delete: 'Radera',
    export: 'Exportera',
    changeArea: 'Ändra plats',
    addToSession: 'Lägg till i pass',
    
    // CatchesPage Redesign - Stats
    contact: 'kontakt',
    total: 'totalt',
    
    // CatchesPage Redesign - Map
    showMarkers: 'Visa markörer',
    showHeatmap: 'Visa heatmap',
    viewDetails: 'Visa detaljer',
    
    // CatchesPage Redesign - Weather Similarity
    findSimilarWeather: 'Hitta liknande väder',
    similarConditions: 'Liknande förhållanden',
    referenceWeather: 'Referensväder',
    foundSimilar: 'Hittade {count} fångster med liknande väder',
    matchScore: 'Matchning',
    viewAllOnMap: 'Visa alla på karta',
    exportSimilar: 'Exportera liknande',
    
    // CatchesPage Redesign - Empty states
    noMatchingCatches: 'Inga fångster matchar filtren',
    clearFiltersToSeeMore: 'Rensa filter för att se fler fångster',
    
    // CatchesPage Redesign - Loading
    loadMore: 'Ladda fler',
  },
  
  en: {
    // Header
    appTitle: 'Fishing & Weather',
    serviceFrom: 'A service from skitfiske.app',
    contactUs: 'Contact us',
    
    // Share
    shareWeather: 'Share weather',
    linkCopied: 'Link copied to clipboard!',
    weatherAppTitle: 'WeatherApp - Weather Forecast',
    checkWeatherFor: 'Check the weather for',
    
    // Welcome Screen
    welcomeTitle: 'Welcome to Fishing & Weather',
    welcomeSubtitle: 'Your smart fishing guide with AI analysis',
    welcomeButton: 'Get Started',
    
    // Map & Controls
    selectLocation: 'Select location on map',
    clickToExplore: 'Explore',
    currentLocationBtn: 'My location',
    locationError: 'Could not get location',
    
    // Date
    selectDate: 'Select date',
    time: 'Time',
    
    // Weather Display
    loading: 'Loading...',
    weather: 'Weather',
    noWeatherData: 'No weather data available',
    temperature: 'Temperature',
    feelsLike: 'Feels like',
    humidity: 'Humidity',
    pressure: 'Pressure',
    wind: 'Wind',
    windGust: 'Gusts',
    precipitation: 'Precipitation',
    visibility: 'Visibility',
    cloudiness: 'Cloudiness',
    sunrise: 'Sunrise',
    sunset: 'Sunset',
    moonPhase: 'Moon Phase',
    sunAndMoon: 'Sun & Moon',
    
    // Weather Conditions
    weatherClear: 'Clear',
    weatherMostlyClear: 'Mostly clear',
    weatherPartlyCloudy: 'Partly cloudy',
    weatherCloudy: 'Cloudy',
    weatherFog: 'Fog',
    weatherRimeFog: 'Rime fog',
    weatherLightDrizzle: 'Light drizzle',
    weatherModerateDrizzle: 'Moderate drizzle',
    weatherDenseDrizzle: 'Dense drizzle',
    weatherLightRain: 'Light rain',
    weatherModerateRain: 'Moderate rain',
    weatherHeavyRain: 'Heavy rain',
    weatherLightSnow: 'Light snow',
    weatherModerateSnow: 'Moderate snow',
    weatherHeavySnow: 'Heavy snow',
    weatherLightShowers: 'Light showers',
    weatherModerateShowers: 'Moderate showers',
    weatherHeavyShowers: 'Heavy showers',
    weatherThunderstorm: 'Thunderstorm',
    weatherThunderstormHail: 'Thunderstorm with hail',
    weatherUnknown: 'Unknown',
    historicalWeather: 'Historical weather',
    currentWeather: 'Current weather',
    forecast: 'Forecast',
    
    // Moon Phases
    newMoon: 'New Moon',
    waxingCrescent: 'Waxing Crescent',
    firstQuarter: 'First Quarter',
    waxingGibbous: 'Waxing Gibbous',
    fullMoon: 'Full Moon',
    waningGibbous: 'Waning Gibbous',
    lastQuarter: 'Last Quarter',
    waningCrescent: 'Waning Crescent',
    
    // Hourly Weather
    hourlyForecast: 'Hourly Forecast',
    hourlyForecastShort: '24h forecast',
    hour: 'Hour',
    
    // Weather History
    weatherHistory: 'Weather History',
    daysBackAndForward: '5 days back & forward from',
    daysBackAndForward2: '5 days back & forward from {date}',
    historicalWeatherDevelopment: 'Historical weather development for selected location',
    astronomy: 'Astronomy',
    maxTemp: 'Max temp',
    minTemp: 'Min temp',
    windSpeed: 'Wind speed',
    windDirection: 'Wind direction',
    windGustLabel: 'Wind gusts',
    timeHours: 'Time (hours)',
    moonPhasePercent: 'Moon phase (%)',
    
    // Weather Trends - Insights
    tempChange: 'Temp change',
    pressureChange: 'Pressure change',
    minPressure: 'Min pressure',
    maxPressure: 'Max pressure',
    avgWind: 'Avg wind',
    maxGust: 'Max gust',
    dominantDirection: 'Dominant direction',
    windSpan: 'Wind span',
    totalPrecipitation: 'Total precipitation',
    maxPerDay: 'Max/day',
    rainyDays: 'Rainy days',
    avgScore: 'Avg score',
    maxScore: 'Max score',
    quality: 'Quality',
    catchDay: 'CATCH DAY',
    catchMomentLegend: 'Green dot = Catch moment',
    
    // Fishing Forecast
    fishingForecast: 'Fishing Forecast',
    advancedAlgorithm: 'Advanced algorithm based on weather data & moon phases',
    fishingScore: 'Fishing Score',
    today: 'today',
    excellentFishing: 'Excellent fishing weather!',
    goodConditions: 'Good conditions',
    moderateConditions: 'Moderate conditions',
    difficultConditions: 'Difficult conditions',
    positiveFactors: 'Positive:',
    negativeFactors: 'Negative:',
    hourByHour: 'Hour by hour',
    upcomingDays: '5 days back & forward from {date}',
    species: 'Species:',
    
    // Fishing factors
    excellentLight: 'Excellent light conditions',
    goodLight: 'Good light for fishing',
    moderateLight: 'Moderate light conditions',
    poorLight: 'Poor light conditions',
    stableHighPressure: 'Stable high pressure favors',
    strongWeatherFront: 'Strong weather front!',
    strongPressureFront: 'Strong pressure front',
    pressureFront: 'Pressure front',
    niceWind: 'Nice wind',
    extremeConditions: 'Extreme conditions!',
    tooHard: 'Too hard!',
    dangerousGusts: 'Dangerously gusty!',
    poorWindConditions: 'Poor wind conditions',
    stronglyGusty: 'Strongly gusty',
    hardGusts: 'Hard gusts',
    problematicWind: 'Problematic wind',
    excellentWeather: 'Excellent weather conditions',
    poorWeatherFront: 'Poor weather: pressure front & hard wind',
    poorWeatherWind: 'Poor weather: too hard wind',
    poorWeatherRain: 'Poor weather: rain/poor visibility',
    poorWeather: 'Poor weather conditions',
    acceptableWeather: 'Acceptable weather conditions',
    goodWaterTemp: 'Good water temp',
    unfavorableWaterTemp: 'Unfavorable water temp',
    moderateWaterTemp: 'Moderate water temp',
    weatherFrontAffects: 'Weather front affects',
    veryStableWeather: 'Very stable weather',
    unstableWeather: 'Unstable weather',
    extremelyWindy: 'Extremely windy - DANGEROUS',
    veryWindy: 'Very windy',
    hardWindy: 'Hard windy',
    dangerousGustsWarning: 'Dangerous gusts',
    extremeRain: 'Extremely heavy rain',
    veryHeavyRain: 'Very heavy rain',
    extremelyPoorVisibility: 'Extremely poor visibility - DANGEROUS',
    veryPoorVisibility: 'Very poor visibility',
    
    // Species names
    pike: 'Pike',
    perch: 'Perch',
    pikeperch: 'Pike-perch',
    zander: 'Zander', // deprecated
    general: 'General',
    trout: 'Trout',
    salmon: 'Salmon',
    seaTrout: 'Sea trout',
    roach: 'Roach',
    bream: 'Bream',
    carp: 'Carp',
    tench: 'Tench',
    ruffe: 'Ruffe',
    crucian: 'Crucian carp',
    other: 'Other',
    
    // AI Recommendation
    alfredAI: 'Alfred AI Fishing Guide',
    aiAnalyzing: 'Alfred is analyzing...',
    aiWaitTime: 'This may take up to 30 seconds ⏱️',
    aiCacheInfo: 'Conversation saved per location, date, language & species • Incl. weather history & moon phase',
    aiWelcomeTitle: 'Hi, my name is Alfred! 👋',
    aiWelcomeIntro: 'I can help you plan your fishing day for',
    aiWelcomePastDate: 'Would you rather plan today\'s fishing trip or a date in the future?',
    aiWelcomeHistorical: 'I use historical weather data and forecasts to give you the best possible advice.',
    aiStartQuestions: 'Start planning',
    aiQuestionIntro: 'Tell Alfred about your fishing for better advice:',
    aiStartChat: 'Start Alfred',
    aiFishingFromShore: 'Fishing from shore?',
    aiFishingMethod: 'Fishing method?',
    aiWaterClarity: 'Water clarity?',
    aiYes: 'Yes',
    aiNo: 'No',
    aiMethodSpin: 'Spin',
    aiMethodBait: 'Bait',
    aiMethodTrolling: 'Trolling',
    aiMethodFly: 'Fly',
    aiMethodVertical: 'Vertical',
    aiMethodLivescope: 'Livescope',
    aiClarityClear: 'Clear',
    aiClarityModerate: 'Moderate',
    aiClarityMurky: 'Murky',
    aiSendMessage: 'Send',
    aiTypingPlaceholder: 'Type a message to Alfred...',
    aiWaitingMessage: 'Hi, let me think for a moment...',
    aiRateLimitTitle: 'Rate Limit Reached',
    aiRateLimitMessage: 'You\'ve reached the maximum number of AI requests (10 per hour). Please try again in {minutes} minute{plural}.',
    aiRateLimitResetAt: 'Reset at:',
    aiErrorTitle: 'Error',
    aiTooManyRequestsTitle: 'Too Many Requests',
    aiTooManyRequestsMessage: 'Please wait a moment before making another request.',
    minute: 'minute',
    minutes: 'minutes',
    
    // Wind Simulator
    windSimulator: 'Wind Simulator',
    hours: 'hours',
    days: 'days',
    play: 'Play',
    pause: 'Pause',
    reset: 'Reset to start',
    speed: 'Speed:',
    
    // Wind strength
    calm: 'Calm',
    light: 'Light',
    moderate: 'Moderate',
    strong: 'Strong',
    gale: 'Gale',
    
    // Wind directions
    north: 'N',
    northeast: 'NE',
    east: 'E',
    southeast: 'SE',
    south: 'S',
    southwest: 'SW',
    west: 'W',
    northwest: 'NW',
    
    // Favorites
    favorites: 'Favorites',
    addToFavorites: 'Add to favorites',
    removeFromFavorites: 'Remove from favorites',
    noFavorites: 'No favorites saved yet',
    
    // Tutorial
    tutorialSkip: 'Skip',
    tutorialNext: 'Next',
    tutorialComplete: 'Complete',
    tutorialMapTitle: '🗺️ Interactive Map',
    tutorialMapDesc: 'Click anywhere on the map to select a location and view weather data.',
    tutorialDateTitle: '📅 Select Date',
    tutorialDateDesc: 'Choose a date to view historical weather or forecast.',
    tutorialLocationTitle: '📍 My Location',
    tutorialLocationDesc: 'Press here to automatically find your current location.',
    tutorialWeatherTitle: '🌤️ Weather Information',
    tutorialWeatherDesc: 'View detailed weather information for selected location and date.',
    tutorialHourlyTitle: '⏰ Hourly Forecast',
    tutorialHourlyDesc: 'Explore the weather forecast hour by hour.',
    tutorialHistoryTitle: '📈 Weather History',
    tutorialHistoryDesc: 'View weather trends 5 days back and forward.',
    tutorialFishingTitle: '🎣 Fishing Forecast',
    tutorialFishingDesc: 'AI-driven fishing forecast based on weather data. Select species for tailored recommendations.',
    tutorialAITitle: '🤖 Alfred Fishing AI',
    tutorialAIDesc: 'Get personalized AI recommendations from Alfred based on all weather conditions. Perfect for both beginners and experienced anglers!',
    tutorialWindTitle: '🌬️ Wind Simulator',
    tutorialWindDesc: 'See animated wind arrows showing direction and strength hour by hour. Play the animation to see how wind changes over time.',
    tutorialFavoritesTitle: '⭐ Favorites',
    tutorialFavoritesDesc: 'Save your favorite locations for quick access.',
    
    // Toast messages
    locationAdded: 'Location added to favorites',
    locationRemoved: 'Location removed from favorites',
    errorOccurred: 'An error occurred',
    errorFetchingWeather: 'Could not fetch weather data',
    
    // Settings
    settings: 'Settings',
    language: 'Language',
    units: 'Units',
    defaultFishSpecies: 'Default fish species',
    metric: 'Metric',
    imperial: 'Imperial',
    saveSettings: 'Save settings',
    settingsUpdated: 'Settings updated',
    exportBackup: 'Export & Backup',
    exportImportData: 'Export / Import Data',
    offlineDataSync: 'Weather data and location names sync automatically when you go online!',
    
    // Footer
    weatherDataFrom: 'Weather data from',
    mapsFrom: 'Maps from',
    builtBy: 'Built by',
    newHere: 'New here?',
    showGuide: 'Show guide',
    
    // Navigation Menu
    navigationWeather: 'Weather',
    catches: 'Catches',
    sessions: 'Sessions',
    areas: 'Areas',
    
    // Catches Page
    noCatchesYet: 'No catches yet',
    noCatchesDescription: 'Press the + button to log your first catch!',
    totalCatches: 'total',
    landed: 'landed',
    released: 'released',
    photos: 'photos',
    loadingCatches: 'Loading catches...',
    unknownLocation: 'Unknown location',
    
    // Catch Detail Page
    backToCatches: 'Back to catches',
    measurements: 'Measurements',
    waterConditions: 'Water Conditions',
    fishingDepth: 'Fishing Depth',
    outOf100: 'out of 100',
    quantity: 'Quantity',
    catch: 'catch',
    notRecorded: 'Not recorded',
    end: 'End',
    
    // Sessions Page
    noSessionsYet: 'No fishing sessions yet',
    noSessionsDescription: 'Start a session by logging a catch!',
    sessionsTotal: 'sessions total',
    active: 'active',
    completed: 'Completed',
    fishingSession: 'Fishing Session',
    start: 'Start',
    duration: 'Duration',
    waterTemp: 'Water Temp',
    loadingSessions: 'Loading sessions...',
    
    // Areas Page
    noAreasYet: 'No areas yet',
    noAreasDescription: 'Fishing areas are created automatically when you log catches!',
    areasVisited: 'visited',
    place: 'place',
    places: 'places',
    coordinates: 'Coordinates',
    radius: 'Radius',
    lastVisit: 'Last visit',
    loadingAreas: 'Loading areas...',
    
    // Tutorial - Navigation step
    tutorialNavigationTitle: 'Navigation & Fishing Log',
    tutorialNavigationDesc: 'Use the menu to switch between weather, your catches, fishing sessions, and areas. Press the + button to quickly log a catch!',
    
    // QuickCatchModal
    quickCatchTitle: '⚡ Quick Log Catch',
    speciesLabel: '🐟 Species',
    typeLabel: '🎯 Type',
    typeCaught: '🎣 Caught',
    typeContact: '💨 Contact',
    countLabel: '🔢 Count',
    moreDetails: 'More details',
    weightLabel: '⚖️ Weight',
    lengthLabel: '📏 Length',
    waterTempLabel: '🌡️ Water Temperature (°C)',
    depthLabel: '📐 Fishing Depth',
    depthPlaceholder: 'Depth',
    photoLabel: '📸 Photo',
    takePhoto: '📷 Take photo',
    chooseFromGallery: '🖼️ Choose from gallery',
    notesLabel: '📝 Notes',
    notesPlaceholder: 'Optional notes...',
    advancedBacklog: '⚙️ Advanced (backlog)',
    dateLabel: '📅 Date',
    datePlaceholder: 'Leave empty for today',
    timeLabel: '⏰ Time',
    timePlaceholder: 'Leave empty for current time',
    sessionLabel: '🎯 Fishing Session',
    autoCreateSession: 'Auto-create new session',
    noSession: 'No session',
    activeSessions: '──── Active Sessions ────',
    completedSessions: '──── Completed Sessions ────',
    areaLabel: '📍 Area',
    selectArea: 'Select area',
    customLocation: 'Custom location',
    activePassSeparator: 'Active Sessions',
    completedPassSeparator: 'Completed Sessions',
    locationLabel: '🗺️ Location',
    chooseOnMap: '📍 Choose on map',
    saveLocation: '💾 Save location',
    cancelLocation: '✕ Cancel',
    logCatch: '✅ Log catch',
    logCatches: '✅ Log catches',
    catchLogged: 'logged',
    catchesLogged: 'logged',
    catchLogFailed: '❌ Could not log catch',
    cannotLogFutureDate: '❌ Cannot log catch in the future',
    photoCaptured: '📷 Photo taken!',
    photoCaptureFailed: '❌ Could not take photo',
    photoUploaded: '🖼️ Photo uploaded!',
    photoUploadFailed: '❌ Could not upload photo',
    photoRemoved: '🗑️ Photo removed',
    selectSpecies: 'Select a species first',
    noArea: 'No location',
    useGPS: 'Use GPS position',
    useGPSDescription: 'Gets your current position',
    customLocationDescription: 'Choose location on map',
    noAreaDescription: 'Save catch without location info',
    sessionAutoCreateDescription: 'Creates new session for this catch',
    sessionNoSessionDescription: 'No connection to session',
    sessionActiveDescription: '🟢 = Active session, ⚪ = Completed session',
    
    // Catch Detail Page
    sunriseLabel: 'Sunrise',
    sunsetLabel: 'Sunset',
    topCatches: 'Top 5',
    excellent: 'Excellent',
    good: 'Good',
    fair: 'Fair',
    keepTrying: 'Keep trying',
    
    // Fishing Methods
    fishingMethod: 'Fishing Method',
    methodSpin: 'Spinning',
    methodJigging: 'Jigging',
    methodTrolling: 'Trolling',
    methodFly: 'Fly Fishing',
    methodBait: 'Bait Fishing',
    methodVertical: 'Vertical',
    methodLivescope: 'Livescope',
    methodIce: 'Ice Fishing',
    
    // Tackle & Bait
    tackle: 'Tackle & Bait',
    tackleWobbler: 'Wobbler',
    tackleSoftbait: 'Soft Bait',
    tackleSpoon: 'Spoon',
    tackleJig: 'Jig',
    tackleCrankbait: 'Crankbait',
    tackleSpinnerbait: 'Spinnerbait',
    tackleTopwater: 'Topwater',
    tackleSwimbait: 'Swimbait',
    tackleDryfly: 'Dry Fly',
    tackleNymph: 'Nymph',
    tackleStreamer: 'Streamer',
    tackleLivebait: 'Live Bait',
    tackleDeadbait: 'Dead Bait',
    tackleWorm: 'Worm',
    tackleMaggot: 'Maggot',
    tackleCorn: 'Corn',
    tackleDough: 'Dough',
    tackleDropshot: 'Dropshot Rig',
    tackleCarolina: 'Carolina Rig',
    tackleTexas: 'Texas Rig',
    
    // Fishing Details UI
    selectMethod: 'Select method',
    selectTackle: 'Select tackle',
    recommendedMethods: 'Recommended',
    recentlyUsed: 'Recently used',
    customTackle: 'Custom tackle',
    addCustomTackle: 'Add custom tackle',
    addMethod: 'Add method',
    manageMethods: 'Manage fishing methods',
    manageTackle: 'Manage tackle',
    manageSpecies: 'Manage species',
    addSpecies: 'Add species',
    added: 'added',
    removed: 'removed',
    error: 'Error',
    defaultLabel: 'default',
    
    // Confirmation dialogs
    confirmRemove: 'Confirm removal',
    cancel: 'Cancel',
    removeSpeciesWarning: 'Are you sure you want to remove this species? Catches with this species will be kept but may have incorrect fishing scores and AI recommendations.',
    removeMethodWarning: 'Are you sure you want to remove this method? Catches with this method will be kept.',
    removeTackleWarning: 'Are you sure you want to remove this tackle? Catches with this tackle will be kept.',
    catchesWillRemain: 'Note: Existing catches will not be affected.',
    
    // Default preferences
    defaultSpecies: 'Default species',
    defaultSpeciesHelp: 'Used for fishing score on weather page and in Alfred AI when logging catches',
    defaultMethod: 'Default method',
    defaultMethodHelp: 'Automatically selected in Alfred AI',
    defaultTackle: 'Default tackle',
    defaultTackleHelp: 'Automatically selected in Alfred AI (can select multiple)',
    clickToSetDefault: 'Click one to set as default',
    clickToSetDefaultMultiple: 'Click to set as default (can select multiple)',
    
    // CatchesPage Redesign - Filter & Search
    search: 'Search',
    clearSearch: 'Clear search',
    searchCatches: 'Search catches...',
    filters: 'Filters',
    clearFilters: 'Clear filters',
    applyFilters: 'Apply filters',
    allDates: 'All dates',
    thisWeek: 'This week',
    thisMonth: 'This month',
    thisYear: 'This year',
    allAreas: 'All areas',
    
    // CatchesPage Redesign - Saved Filters
    allCatches: 'All Catches',
    createNewFilter: 'Create New Filter',
    configure: 'Configure',
    saveFilter: 'Save Filter',
    updateFilter: 'Update Filter',
    deleteFilter: 'Delete Filter',
    filterName: 'Filter Name',
    filterIcon: 'Icon',
    noSavedFilters: 'No saved filters',
    
    // CatchesPage Redesign - Advanced Filters
    methods: 'Methods',
    baits: 'Baits',
    seasons: 'Seasons',
    spring: 'Spring',
    summer: 'Summer',
    fall: 'Fall',
    winter: 'Winter',
    timeOfDay: 'Time of Day',
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',
    night: 'Night',
    weatherFilters: 'Weather Filters',
    windDirections: 'Wind Directions',
    
    // CatchesPage Redesign - Sort & Group
    groupBy: 'Group by',
    sortBy: 'Sort by',
    date: 'Date',
    area: 'Area',
    session: 'Session',
    none: 'None',
    newest: 'Newest',
    oldest: 'Oldest',
    weight: 'Weight',
    length: 'Length',
    
    // CatchesPage Redesign - View modes
    view: 'View',
    list: 'List',
    grid: 'Grid',
    map: 'Map',
    
    // CatchesPage Redesign - Selection & Bulk
    selectMultiple: 'Select multiple',
    selected: 'selected',
    delete: 'Delete',
    export: 'Export',
    changeArea: 'Change area',
    addToSession: 'Add to session',
    
    // CatchesPage Redesign - Stats
    contact: 'contact',
    total: 'total',
    
    // CatchesPage Redesign - Map
    showMarkers: 'Show markers',
    showHeatmap: 'Show heatmap',
    viewDetails: 'View details',
    
    // CatchesPage Redesign - Weather Similarity
    findSimilarWeather: 'Find similar weather',
    similarConditions: 'Similar conditions',
    referenceWeather: 'Reference weather',
    foundSimilar: 'Found {count} catches with similar weather',
    matchScore: 'Match',
    viewAllOnMap: 'View all on map',
    exportSimilar: 'Export similar',
    
    // CatchesPage Redesign - Empty states
    noMatchingCatches: 'No catches match the filters',
    clearFiltersToSeeMore: 'Clear filters to see more catches',
    
    // CatchesPage Redesign - Loading
    loadMore: 'Load more',
  },
  
  da: {
    // Header
    appTitle: 'Fiskeri & Vejr',
    serviceFrom: 'En tjeneste fra skitfiske.app',
    contactUs: 'Kontakt os',
    
    // Share
    shareWeather: 'Del vejr',
    linkCopied: 'Link kopieret til udklipsholder!',
    weatherAppTitle: 'VejrApp - Vejrudsigt',
    checkWeatherFor: 'Tjek vejret for',
    
    // Welcome Screen
    welcomeTitle: 'Velkommen til Fiskeri & Vejr',
    welcomeSubtitle: 'Din smarte fiskeguide med AI-analyse',
    welcomeButton: 'Kom i gang',
    
    // Map & Controls
    selectLocation: 'Vælg placering på kort',
    clickToExplore: 'Udforsk',
    currentLocationBtn: 'Min position',
    locationError: 'Kunne ikke hente position',
    
    // Date
    selectDate: 'Vælg dato',
    time: 'Tid',
    
    // Weather Display
    loading: 'Indlæser...',
    weather: 'Vejr',
    noWeatherData: 'Ingen vejrdata tilgængelig',
    temperature: 'Temperatur',
    feelsLike: 'Føles som',
    humidity: 'Luftfugtighed',
    pressure: 'Lufttryk',
    wind: 'Vind',
    windGust: 'Vindstød',
    precipitation: 'Nedbør',
    visibility: 'Sigtbarhed',
    cloudiness: 'Skydække',
    sunrise: 'Solopgang',
    sunset: 'Solnedgang',
    moonPhase: 'Månefase',
    sunAndMoon: 'Sol & Måne',
    
    // Weather Conditions
    weatherClear: 'Klart',
    weatherMostlyClear: 'Mest klart',
    weatherPartlyCloudy: 'Delvis overskyet',
    weatherCloudy: 'Overskyet',
    weatherFog: 'Tåge',
    weatherRimeFog: 'Rimtåge',
    weatherLightDrizzle: 'Let støvregn',
    weatherModerateDrizzle: 'Moderat støvregn',
    weatherDenseDrizzle: 'Tæt støvregn',
    weatherLightRain: 'Let regn',
    weatherModerateRain: 'Moderat regn',
    weatherHeavyRain: 'Kraftig regn',
    weatherLightSnow: 'Let sne',
    weatherModerateSnow: 'Moderat sne',
    weatherHeavySnow: 'Kraftig sne',
    weatherLightShowers: 'Lette byger',
    weatherModerateShowers: 'Moderate byger',
    weatherHeavyShowers: 'Kraftige byger',
    weatherThunderstorm: 'Tordenvejr',
    weatherThunderstormHail: 'Tordenvejr med hagl',
    weatherUnknown: 'Ukendt',
    historicalWeather: 'Historisk vejr',
    currentWeather: 'Aktuelt vejr',
    forecast: 'Prognose',
    
    // Moon Phases
    newMoon: 'Nymåne',
    waxingCrescent: 'Tiltagende månesigel',
    firstQuarter: 'Første kvarter',
    waxingGibbous: 'Tiltagende halvmåne',
    fullMoon: 'Fuldmåne',
    waningGibbous: 'Aftagende halvmåne',
    lastQuarter: 'Sidste kvarter',
    waningCrescent: 'Aftagende månesigel',
    
    // Hourly Weather
    hourlyForecast: 'Timeprognose',
    hourlyForecastShort: '24t prognose',
    hour: 'Time',
    
            
    // Weather History
    weatherHistory: 'Vejrhistorik',
    daysBackAndForward: '5 dage tilbage & frem fra',
    daysBackAndForward2: '5 dage tilbage & frem fra {date}',
    historicalWeatherDevelopment: 'Historisk vejrudvikling for valgt placering',
    astronomy: 'Astronomi',
    maxTemp: 'Maks temp',
    minTemp: 'Min temp',
    windSpeed: 'Vindhastighed',
    windDirection: 'Vindretning',
    windGustLabel: 'Vindstød',
    timeHours: 'Tid (timer)',
    moonPhasePercent: 'Månefase (%)',
    
    // Weather Trends - Insights
    tempChange: 'Temp ændring',
    pressureChange: 'Tryk ændring',
    minPressure: 'Min tryk',
    maxPressure: 'Maks tryk',
    avgWind: 'Gnmst vind',
    maxGust: 'Maks vindstød',
    dominantDirection: 'Dominerende retning',
    windSpan: 'Vind spænd',
    totalPrecipitation: 'Total nedbør',
    maxPerDay: 'Maks/dag',
    rainyDays: 'Regndage',
    avgScore: 'Gnmst score',
    maxScore: 'Maks score',
    quality: 'Kvalitet',
    catchDay: 'FANGSTDAG',
    catchMomentLegend: 'Grøn prik = Fangstøjeblik',
    
    
    // Fishing Forecast
    fishingForecast: 'Fiskeprognose',
    advancedAlgorithm: 'Avanceret algoritme baseret på vejrdata & månefaser',
    fishingScore: 'Fiskescore',
    today: 'i dag',
    excellentFishing: 'Fremragende fiskevejr!',
    goodConditions: 'Gode forhold',
    moderateConditions: 'Moderate forhold',
    difficultConditions: 'Vanskelige forhold',
    positiveFactors: 'Positive:',
    negativeFactors: 'Negative:',
    hourByHour: 'Time for time',
    upcomingDays: '5 dage tilbage & fremad fra {date}',
    species: 'Art:',
    
    // Fishing factors
    excellentLight: 'Fremragende lysforhold',
    goodLight: 'Godt lys til fiskeri',
    moderateLight: 'Moderate lysforhold',
    poorLight: 'Dårlige lysforhold',
    stableHighPressure: 'Stabilt højtryk favoriserer',
    strongWeatherFront: 'Kraftig vejrfront!',
    strongPressureFront: 'Stærk trykfront',
    pressureFront: 'Trykfront',
    niceWind: 'Fin vind',
    extremeConditions: 'Ekstreme forhold!',
    tooHard: 'For hårdt!',
    dangerousGusts: 'Farligt vindstød!',
    poorWindConditions: 'Dårlige vindforhold',
    stronglyGusty: 'Kraftigt blæsende',
    hardGusts: 'Hårde vindstød',
    problematicWind: 'Problematisk vind',
    excellentWeather: 'Fremragende vejrforhold',
    poorWeatherFront: 'Dårligt vejr: trykfront & hård vind',
    poorWeatherWind: 'Dårligt vejr: for hård vind',
    poorWeatherRain: 'Dårligt vejr: regn/dårlig sigtbarhed',
    poorWeather: 'Dårlige vejrforhold',
    acceptableWeather: 'Acceptable vejrforhold',
    goodWaterTemp: 'God vandtemperatur',
    unfavorableWaterTemp: 'Ugunstig vandtemperatur',
    moderateWaterTemp: 'Moderat vandtemperatur',
    weatherFrontAffects: 'Vejrfront påvirker',
    veryStableWeather: 'Meget stabilt vejr',
    unstableWeather: 'Ustabilt vejr',
    extremelyWindy: 'Ekstremt blæsende - FARLIGT',
    veryWindy: 'Meget blæsende',
    hardWindy: 'Hårdt blæsende',
    dangerousGustsWarning: 'Farlige vindstød',
    extremeRain: 'Ekstremt kraftig regn',
    veryHeavyRain: 'Meget kraftig regn',
    extremelyPoorVisibility: 'Ekstremt dårlig sigtbarhed - FARLIGT',
    veryPoorVisibility: 'Meget dårlig sigtbarhed',
    
    // Species names
    pike: 'Gedde',
    perch: 'Aborre',
    pikeperch: 'Sandart',
    zander: 'Sandart', // deprecated
    general: 'Generel',
    trout: 'Ørred',
    salmon: 'Laks',
    seaTrout: 'Havørred',
    roach: 'Skalle',
    bream: 'Brasen',
    carp: 'Karpe',
    tench: 'Suder',
    ruffe: 'Hork',
    crucian: 'Karusse',
    other: 'Andet',
    
    // AI Recommendation
    alfredAI: 'Alfred AI Fiskeguide',
    aiAnalyzing: 'Alfred analyserer...',
    aiWaitTime: 'Dette kan tage op til 30 sekunder ⏱️',
    aiCacheInfo: 'Samtale gemt per sted, dato, sprog & art • Inkl. vejrhistorik & månefase',
    aiWelcomeTitle: 'Hej, mit navn er Alfred! 👋',
    aiWelcomeIntro: 'Jeg kan hjælpe dig med at planlægge din fiskedag for',
    aiWelcomePastDate: 'Vil du hellere planlægge dagens fisketur eller en fremtidig dato?',
    aiWelcomeHistorical: 'Jeg bruger historiske vejrdata og prognoser for at give dig det bedste råd.',
    aiStartQuestions: 'Begynd planlægning',
    aiQuestionIntro: 'Fortæl Alfred om dit fiskeri for bedre råd:',
    aiStartChat: 'Start Alfred',
    aiFishingFromShore: 'Fisker du fra land?',
    aiFishingMethod: 'Fiskemetode?',
    aiWaterClarity: 'Vandklarhed?',
    aiYes: 'Ja',
    aiNo: 'Nej',
    aiMethodSpin: 'Spind',
    aiMethodBait: 'Agn',
    aiMethodTrolling: 'Trolling',
    aiMethodFly: 'Flue',
    aiMethodVertical: 'Vertikal',
    aiMethodLivescope: 'Livescope',
    aiClarityClear: 'Klar',
    aiClarityModerate: 'Moderat',
    aiClarityMurky: 'Grumset',
    aiSendMessage: 'Send',
    aiTypingPlaceholder: 'Skriv en besked til Alfred...',
    aiWaitingMessage: 'Hej, jeg tænker et øjeblik...',
    aiRateLimitTitle: 'Grænse nået',
    aiRateLimitMessage: 'Du har nået det maksimale antal AI-anmodninger (10 per time). Prøv igen om {minutes} minut{plural}.',
    aiRateLimitResetAt: 'Nulstilles kl:',
    aiErrorTitle: 'Fejl',
    aiTooManyRequestsTitle: 'For mange anmodninger',
    aiTooManyRequestsMessage: 'Vent venligst et øjeblik før du laver en ny anmodning.',
    minute: 'minut',
    minutes: 'minutter',
    
    // Wind Simulator
    windSimulator: 'Vindsimulator',
    hours: 'timer',
    days: 'dage',
    play: 'Afspil',
    pause: 'Pause',
    reset: 'Nulstil til start',
    speed: 'Hastighed:',
    
    // Wind strength
    calm: 'Stille',
    light: 'Let',
    moderate: 'Moderat',
    strong: 'Kraftig',
    gale: 'Storm',
    
    // Wind directions
    north: 'N',
    northeast: 'NØ',
    east: 'Ø',
    southeast: 'SØ',
    south: 'S',
    southwest: 'SV',
    west: 'V',
    northwest: 'NV',
    
    // Favorites
    favorites: 'Favoritter',
    addToFavorites: 'Tilføj til favoritter',
    removeFromFavorites: 'Fjern fra favoritter',
    noFavorites: 'Ingen favoritter gemt endnu',
    
    // Tutorial
    tutorialSkip: 'Spring over',
    tutorialNext: 'Næste',
    tutorialComplete: 'Fuldfør',
    tutorialMapTitle: '🗺️ Interaktivt kort',
    tutorialMapDesc: 'Klik hvor som helst på kortet for at vælge en placering og se vejrdata.',
    tutorialDateTitle: '📅 Vælg dato',
    tutorialDateDesc: 'Vælg en dato for at se historisk vejr eller prognose.',
    tutorialLocationTitle: '📍 Min position',
    tutorialLocationDesc: 'Tryk her for automatisk at finde din nuværende position.',
    tutorialWeatherTitle: '🌤️ Vejrinformation',
    tutorialWeatherDesc: 'Se detaljeret vejrinformation for valgt placering og dato.',
    tutorialHourlyTitle: '⏰ Timeprognose',
    tutorialHourlyDesc: 'Udforsk vejrprognosen time for time.',
    tutorialHistoryTitle: '📈 Vejrhistorik',
    tutorialHistoryDesc: 'Se vejrudviklingen 5 dage tilbage og fremad.',
    tutorialFishingTitle: '🎣 Fiskeprognose',
    tutorialFishingDesc: 'AI-drevet fiskeprognose baseret på vejrdata. Vælg fiskeart for skræddersyede anbefalinger.',
    tutorialAITitle: '🤖 Alfred Fiske-AI',
    tutorialAIDesc: 'Få personlige AI-anbefalinger fra Alfred baseret på alle vejrforhold. Perfekt til både begyndere og erfarne fiskere!',
    tutorialWindTitle: '🌬️ Vindsimulator',
    tutorialWindDesc: 'Se animerede vindpile, der viser retning og styrke time for time. Afspil animationen for at se, hvordan vinden ændrer sig over tid.',
    tutorialFavoritesTitle: '⭐ Favoritter',
    tutorialFavoritesDesc: 'Gem dine favoritsteder for hurtig adgang.',
    
    // Toast messages
    locationAdded: 'Placering tilføjet til favoritter',
    locationRemoved: 'Placering fjernet fra favoritter',
    errorOccurred: 'Der opstod en fejl',
    errorFetchingWeather: 'Kunne ikke hente vejrdata',
    
    // Settings
    settings: 'Indstillinger',
    language: 'Sprog',
    units: 'Enheder',
    defaultFishSpecies: 'Standard fiskeart',
    metric: 'Metrisk',
    imperial: 'Imperial',
    saveSettings: 'Gem indstillinger',
    settingsUpdated: 'Indstillinger opdateret',
    exportBackup: 'Eksporter & Backup',
    exportImportData: 'Eksporter / Importer Data',
    offlineDataSync: 'Vejrdata og stednavne synkroniseres automatisk, når du går online!',
    
    // Footer
    weatherDataFrom: 'Vejrdata fra',
    mapsFrom: 'Kort fra',
    builtBy: 'Bygget af',
    newHere: 'Ny her?',
    showGuide: 'Vis guide',
    
    // Navigation Menu
    navigationWeather: 'Vejr',
    catches: 'Fangster',
    sessions: 'Fiskesessioner',
    areas: 'Områder',
    
    // Catches Page
    noCatchesYet: 'Ingen fangster endnu',
    noCatchesDescription: 'Tryk på +-knappen for at logge din første fangst!',
    totalCatches: 'i alt',
    landed: 'landede',
    released: 'sluppet',
    photos: 'fotos',
    loadingCatches: 'Indlæser fangster...',
    unknownLocation: 'Ukendt placering',
    
    // Catch Detail Page
    backToCatches: 'Tilbage til fangster',
    measurements: 'Målinger',
    waterConditions: 'Vandforhold',
    fishingDepth: 'Fiskedybde',
    outOf100: 'ud af 100',
    quantity: 'Antal',
    catch: 'fangst',
    notRecorded: 'Ikke registreret',
    end: 'Slut',
    
    // Sessions Page
    noSessionsYet: 'Ingen fiskesessioner endnu',
    noSessionsDescription: 'Start en session ved at logge en fangst!',
    sessionsTotal: 'sessioner i alt',
    active: 'aktive',
    completed: 'Afsluttet',
    fishingSession: 'Fiskesession',
    start: 'Start',
    duration: 'Varighed',
    waterTemp: 'Vandtemp',
    loadingSessions: 'Indlæser sessioner...',
    
    // Areas Page
    noAreasYet: 'Ingen områder endnu',
    noAreasDescription: 'Fiskeområder oprettes automatisk, når du logger fangster!',
    areasVisited: 'besøgte',
    place: 'sted',
    places: 'steder',
    coordinates: 'Koordinater',
    radius: 'Radius',
    lastVisit: 'Sidste besøg',
    loadingAreas: 'Indlæser områder...',
    
    // Tutorial - Navigation step
    tutorialNavigationTitle: 'Navigation & Fiskelog',
    tutorialNavigationDesc: 'Brug menuen til at skifte mellem vejr, dine fangster, fiskesessioner og områder. Tryk på +-knappen for hurtigt at logge en fangst!',
    
    // QuickCatchModal
    quickCatchTitle: '⚡ Hurtiglog Fangst',
    speciesLabel: '🐟 Art',
    typeLabel: '🎯 Type',
    typeCaught: '🎣 Fanget',
    typeContact: '💨 Kontakt',
    countLabel: '🔢 Antal',
    moreDetails: 'Flere detaljer',
    weightLabel: '⚖️ Vægt',
    lengthLabel: '📏 Længde',
    waterTempLabel: '🌡️ Vandtemperatur (°C)',
    depthLabel: '📐 Fiskedybde',
    depthPlaceholder: 'Dybde',
    photoLabel: '📸 Foto',
    takePhoto: '📷 Tag foto',
    chooseFromGallery: '🖼️ Vælg fra galleri',
    notesLabel: '📝 Noter',
    notesPlaceholder: 'Valgfrie noter...',
    advancedBacklog: '⚙️ Avanceret (backlog)',
    dateLabel: '📅 Dato',
    datePlaceholder: 'Lad stå tom for dagens dato',
    timeLabel: '⏰ Tid',
    timePlaceholder: 'Lad stå tom for aktuel tid',
    sessionLabel: '🎯 Fiskesession',
    autoCreateSession: 'Auto-opret ny session',
    noSession: 'Ingen session',
    activeSessions: '──── Aktive Sessioner ────',
    completedSessions: '──── Afsluttede Sessioner ────',
    areaLabel: '📍 Område',
    selectArea: 'Vælg område',
    customLocation: 'Brugerdefineret placering',
    activePassSeparator: 'Aktive Sessioner',
    completedPassSeparator: 'Afsluttede Sessioner',
    locationLabel: '🗺️ Placering',
    chooseOnMap: '📍 Vælg på kort',
    saveLocation: '💾 Gem placering',
    cancelLocation: '✕ Annuller',
    logCatch: '✅ Log fangst',
    logCatches: '✅ Log fangster',
    catchLogged: 'logget',
    catchesLogged: 'logget',
    catchLogFailed: '❌ Kunne ikke logge fangst',
    cannotLogFutureDate: '❌ Kan ikke logge fangst i fremtiden',
    photoCaptured: '📷 Foto taget!',
    photoCaptureFailed: '❌ Kunne ikke tage foto',
    photoUploaded: '🖼️ Foto uploadet!',
    photoUploadFailed: '❌ Kunne ikke uploade foto',
    photoRemoved: '🗑️ Foto fjernet',
    selectSpecies: 'Vælg en art først',
    noArea: 'Ingen placering',
    useGPS: 'Brug GPS-position',
    useGPSDescription: 'Henter din nuværende position',
    customLocationDescription: 'Vælg placering på kort',
    noAreaDescription: 'Gem fangst uden placeringsinfo',
    sessionAutoCreateDescription: 'Opretter ny session til denne fangst',
    sessionNoSessionDescription: 'Ingen forbindelse til session',
    sessionActiveDescription: '🟢 = Aktiv session, ⚪ = Afsluttet session',
    
    // Catch Detail Page
    sunriseLabel: 'Solopgang',
    sunsetLabel: 'Solnedgang',
    topCatches: 'Top 5',
    excellent: 'Fremragende',
    good: 'God',
    fair: 'Acceptabelt',
    keepTrying: 'Fortsæt med at prøve',
    
    // Fishing Methods
    fishingMethod: 'Fiskemetode',
    methodSpin: 'Spinning',
    methodJigging: 'Jigging',
    methodTrolling: 'Trolling',
    methodFly: 'Fluefiskeri',
    methodBait: 'Agnfiskeri',
    methodVertical: 'Vertikal',
    methodLivescope: 'Livescope',
    methodIce: 'Isfiskeri',
    
    // Tackle & Bait
    tackle: 'Udstyr & Agn',
    tackleWobbler: 'Wobbler',
    tackleSoftbait: 'Gummiagn',
    tackleSpoon: 'Spinner',
    tackleJig: 'Jig',
    tackleCrankbait: 'Crankbait',
    tackleSpinnerbait: 'Spinnerbait',
    tackleTopwater: 'Topwater',
    tackleSwimbait: 'Swimbait',
    tackleDryfly: 'Tørflue',
    tackleNymph: 'Nymfe',
    tackleStreamer: 'Streamer',
    tackleLivebait: 'Levende agn',
    tackleDeadbait: 'Dødt agn',
    tackleWorm: 'Orm',
    tackleMaggot: 'Made',
    tackleCorn: 'Majs',
    tackleDough: 'Dej',
    tackleDropshot: 'Dropshot-rig',
    tackleCarolina: 'Carolina-rig',
    tackleTexas: 'Texas-rig',
    
    // Fishing Details UI
    selectMethod: 'Vælg metode',
    selectTackle: 'Vælg agn',
    recommendedMethods: 'Anbefalet',
    recentlyUsed: 'Senest anvendt',
    customTackle: 'Brugerdefineret agn',
    addCustomTackle: 'Tilføj eget agn',
    addMethod: 'Tilføj metode',
    manageMethods: 'Administrer fiskemetoder',
    manageTackle: 'Administrer agn',
    manageSpecies: 'Administrer arter',
    addSpecies: 'Tilføj art',
    added: 'tilføjet',
    removed: 'fjernet',
    error: 'Fejl',
    defaultLabel: 'standard',
    
    // Confirmation dialogs
    confirmRemove: 'Bekræft fjernelse',
    cancel: 'Annuller',
    removeSpeciesWarning: 'Er du sikker på, at du vil fjerne denne art? Fangster med denne art vil blive bevaret, men kan få forkert fiskescore og AI-anbefalinger.',
    removeMethodWarning: 'Er du sikker på, at du vil fjerne denne metode? Fangster med denne metode vil blive bevaret.',
    removeTackleWarning: 'Er du sikker på, at du vil fjerne dette agn? Fangster med dette agn vil blive bevaret.',
    catchesWillRemain: 'Bemærk: Eksisterende fangster påvirkes ikke.',
    
    // Default preferences
    defaultSpecies: 'Standard art',
    defaultSpeciesHelp: 'Bruges til fiskescore på vejr-siden og i Alfred AI når du logger fangst',
    defaultMethod: 'Standard metode',
    defaultMethodHelp: 'Vælges automatisk i Alfred AI',
    defaultTackle: 'Standard agn',
    defaultTackleHelp: 'Vælges automatisk i Alfred AI (kan vælge flere)',
    clickToSetDefault: 'Klik på en for at sætte som standard',
    clickToSetDefaultMultiple: 'Klik for at sætte som standard (kan vælge flere)',
    
    // CatchesPage Redesign - Filter & Search
    search: 'Søg',
    clearSearch: 'Ryd søgning',
    searchCatches: 'Søg fangster...',
    filters: 'Filtre',
    clearFilters: 'Ryd filtre',
    applyFilters: 'Vis resultater',
    allDates: 'Alle datoer',
    thisWeek: 'Denne uge',
    thisMonth: 'Denne måned',
    thisYear: 'Dette år',
    allAreas: 'Alle steder',
    
    // CatchesPage Redesign - Saved Filters
    allCatches: 'Alle fangster',
    createNewFilter: 'Opret nyt filter',
    configure: 'Konfigurer',
    saveFilter: 'Gem filter',
    updateFilter: 'Opdater filter',
    deleteFilter: 'Slet filter',
    filterName: 'Filternavn',
    filterIcon: 'Ikon',
    noSavedFilters: 'Ingen gemte filtre',
    
    // CatchesPage Redesign - Advanced Filters
    methods: 'Metoder',
    baits: 'Agn',
    seasons: 'Årstider',
    spring: 'Forår',
    summer: 'Sommer',
    fall: 'Efterår',
    winter: 'Vinter',
    timeOfDay: 'Tid på dagen',
    morning: 'Morgen',
    afternoon: 'Eftermiddag',
    evening: 'Aften',
    night: 'Nat',
    weatherFilters: 'Vejrfiltre',
    windDirections: 'Vindretninger',
    
    // CatchesPage Redesign - Sort & Group
    groupBy: 'Gruppér efter',
    sortBy: 'Sortér efter',
    date: 'Dato',
    area: 'Sted',
    session: 'Session',
    none: 'Ingen',
    newest: 'Nyeste',
    oldest: 'Ældste',
    weight: 'Vægt',
    length: 'Længde',
    
    // CatchesPage Redesign - View modes
    view: 'Visning',
    list: 'Liste',
    grid: 'Gitter',
    map: 'Kort',
    
    // CatchesPage Redesign - Selection & Bulk
    selectMultiple: 'Vælg flere',
    selected: 'valgte',
    delete: 'Slet',
    export: 'Eksportér',
    changeArea: 'Skift sted',
    addToSession: 'Tilføj til session',
    
    // CatchesPage Redesign - Stats
    contact: 'kontakt',
    total: 'total',
    
    // CatchesPage Redesign - Map
    showMarkers: 'Vis markører',
    showHeatmap: 'Vis heatmap',
    viewDetails: 'Se detaljer',
    
    // CatchesPage Redesign - Weather Similarity
    findSimilarWeather: 'Find lignende vejr',
    similarConditions: 'Lignende forhold',
    referenceWeather: 'Referencevejr',
    foundSimilar: 'Fandt {count} fangster med lignende vejr',
    matchScore: 'Match',
    viewAllOnMap: 'Se alle på kort',
    exportSimilar: 'Eksportér lignende',
    
    // CatchesPage Redesign - Empty states
    noMatchingCatches: 'Ingen fangster matcher filtrene',
    clearFiltersToSeeMore: 'Ryd filtre for at se flere fangster',
    
    // CatchesPage Redesign - Loading
    loadMore: 'Indlæs flere',
  },
  
  no: {
    // Header
    appTitle: 'Fiske & Vær',
    serviceFrom: 'En tjeneste fra skitfiske.app',
    contactUs: 'Kontakt oss',
    
    // Share
    shareWeather: 'Del vær',
    linkCopied: 'Lenke kopiert til utklippstavle!',
    weatherAppTitle: 'VærApp - Værvarsel',
    checkWeatherFor: 'Sjekk været for',
    
    // Welcome Screen
    welcomeTitle: 'Velkommen til Fiske & Vær',
    welcomeSubtitle: 'Din smarte fiskeguide med AI-analyse',
    welcomeButton: 'Kom i gang',
    
    // Map & Controls
    selectLocation: 'Velg plassering på kart',
    clickToExplore: 'Utforsk',
    currentLocationBtn: 'Min posisjon',
    locationError: 'Kunne ikke hente posisjon',
    
    // Date
    selectDate: 'Velg dato',
    time: 'Tid',
    
    // Weather Display
    loading: 'Laster...',
    weather: 'Vær',
    noWeatherData: 'Ingen værdata tilgjengelig',
    temperature: 'Temperatur',
    feelsLike: 'Føles som',
    humidity: 'Luftfuktighet',
    pressure: 'Lufttrykk',
    wind: 'Vind',
    windGust: 'Vindkast',
    precipitation: 'Nedbør',
    visibility: 'Sikt',
    cloudiness: 'Skydekke',
    sunrise: 'Soloppgang',
    sunset: 'Solnedgang',
    moonPhase: 'Månefase',
    sunAndMoon: 'Sol & Måne',
    
    // Weather Conditions
    weatherClear: 'Klart',
    weatherMostlyClear: 'Mest klart',
    weatherPartlyCloudy: 'Delvis skyet',
    weatherCloudy: 'Overskyet',
    weatherFog: 'Tåke',
    weatherRimeFog: 'Rimtåke',
    weatherLightDrizzle: 'Lett yr',
    weatherModerateDrizzle: 'Moderat yr',
    weatherDenseDrizzle: 'Tett yr',
    weatherLightRain: 'Lett regn',
    weatherModerateRain: 'Moderat regn',
    weatherHeavyRain: 'Kraftig regn',
    weatherLightSnow: 'Lett snø',
    weatherModerateSnow: 'Moderat snø',
    weatherHeavySnow: 'Kraftig snø',
    weatherLightShowers: 'Lette byger',
    weatherModerateShowers: 'Moderate byger',
    weatherHeavyShowers: 'Kraftige byger',
    weatherThunderstorm: 'Tordenvær',
    weatherThunderstormHail: 'Tordenvær med hagl',
    weatherUnknown: 'Ukjent',
    historicalWeather: 'Historisk vær',
    currentWeather: 'Aktuelt vær',
    forecast: 'Prognose',
    
    // Moon Phases
    newMoon: 'Nymåne',
    waxingCrescent: 'Voksende månesigd',
    firstQuarter: 'Første kvarter',
    waxingGibbous: 'Voksende halvmåne',
    fullMoon: 'Fullmåne',
    waningGibbous: 'Avtagende halvmåne',
    lastQuarter: 'Siste kvarter',
    waningCrescent: 'Avtagende månesigd',
    
    // Hourly Weather
    hourlyForecast: 'Timeprognose',
    hourlyForecastShort: '24t prognose',
    hour: 'Time',
    
    // Weather History
    weatherHistory: 'Værhistorikk',
    daysBackAndForward: '5 dager tilbake & fremover fra',
    daysBackAndForward2: '5 dager tilbake & fremover fra {date}',
    historicalWeatherDevelopment: 'Historisk værutvikling for valgt sted',
    astronomy: 'Astronomi',
    maxTemp: 'Maks temp',
    minTemp: 'Min temp',
    windSpeed: 'Vindhastighet',
    windDirection: 'Vindretning',
    windGustLabel: 'Vindkast',
    timeHours: 'Tid (timer)',
    moonPhasePercent: 'Månefase (%)',
    
    // Weather Trends - Insights
    tempChange: 'Temp endring',
    pressureChange: 'Trykk endring',
    minPressure: 'Min trykk',
    maxPressure: 'Maks trykk',
    avgWind: 'Gjsnt vind',
    maxGust: 'Maks vindkast',
    dominantDirection: 'Dominerende retning',
    windSpan: 'Vind spenn',
    totalPrecipitation: 'Total nedbør',
    maxPerDay: 'Maks/dag',
    rainyDays: 'Regndager',
    avgScore: 'Gjsnt score',
    maxScore: 'Maks score',
    quality: 'Kvalitet',
    catchDay: 'FANGSTDAG',
    catchMomentLegend: 'Grønn prikk = Fangstøyeblikk',
    
    
    // Fishing Forecast
    fishingForecast: 'Fiskeprognose',
    advancedAlgorithm: 'Avansert algoritme basert på værdata & månefaser',
    fishingScore: 'Fiskescore',
    today: 'i dag',
    excellentFishing: 'Utmerket fiskevær!',
    goodConditions: 'Gode forhold',
    moderateConditions: 'Moderate forhold',
    difficultConditions: 'Vanskelige forhold',
    positiveFactors: 'Positive:',
    negativeFactors: 'Negative:',
    hourByHour: 'Time for time',
    upcomingDays: '5 dager tilbake & fremover fra {date}',
    species: 'Art:',
    
    // Fishing factors
    excellentLight: 'Utmerkede lysforhold',
    goodLight: 'Godt lys for fiske',
    moderateLight: 'Moderate lysforhold',
    poorLight: 'Dårlige lysforhold',
    stableHighPressure: 'Stabilt høytrykk favoriserer',
    strongWeatherFront: 'Kraftig værfront!',
    strongPressureFront: 'Sterk trykkfront',
    pressureFront: 'Trykkfront',
    niceWind: 'Fin vind',
    extremeConditions: 'Ekstreme forhold!',
    tooHard: 'For hardt!',
    dangerousGusts: 'Farlig vindkast!',
    poorWindConditions: 'Dårlige vindforhold',
    stronglyGusty: 'Kraftig vindkast',
    hardGusts: 'Harde vindkast',
    problematicWind: 'Problematisk vind',
    excellentWeather: 'Utmerkede værforhold',
    poorWeatherFront: 'Dårlig vær: trykkfront & hard vind',
    poorWeatherWind: 'Dårlig vær: for hard vind',
    poorWeatherRain: 'Dårlig vær: regn/dårlig sikt',
    poorWeather: 'Dårlige værforhold',
    acceptableWeather: 'Akseptable værforhold',
    goodWaterTemp: 'God vanntemperatur',
    unfavorableWaterTemp: 'Ugunstig vanntemperatur',
    moderateWaterTemp: 'Moderat vanntemperatur',
    weatherFrontAffects: 'Værfront påvirker',
    veryStableWeather: 'Meget stabilt vær',
    unstableWeather: 'Ustabilt vær',
    extremelyWindy: 'Ekstremt blåsende - FARLIG',
    veryWindy: 'Meget blåsende',
    hardWindy: 'Hardt blåsende',
    dangerousGustsWarning: 'Farlige vindkast',
    extremeRain: 'Ekstremt kraftig regn',
    veryHeavyRain: 'Meget kraftig regn',
    extremelyPoorVisibility: 'Ekstremt dårlig sikt - FARLIG',
    veryPoorVisibility: 'Meget dårlig sikt',
    
    // Species names
    pike: 'Gjedde',
    perch: 'Abbor',
    pikeperch: 'Gjørs',
    zander: 'Gjørs', // deprecated
    general: 'Generell',
    trout: 'Ørret',
    salmon: 'Laks',
    seaTrout: 'Sjøørret',
    roach: 'Mort',
    bream: 'Brasme',
    carp: 'Karpe',
    tench: 'Suter',
    ruffe: 'Hork',
    crucian: 'Karuss',
    other: 'Annet',
    
    // AI Recommendation
    alfredAI: 'Alfred AI Fiskeguide',
    aiAnalyzing: 'Alfred analyserer...',
    aiWaitTime: 'Dette kan ta opptil 30 sekunder ⏱️',
    aiCacheInfo: 'Samtale lagret per sted, dato, språk & art • Inkl. værhistorikk & månefase',
    aiWelcomeTitle: 'Hei, mitt navn er Alfred! 👋',
    aiWelcomeIntro: 'Jeg kan hjelpe deg med å planlegge fiskedagen din for',
    aiWelcomePastDate: 'Vil du heller planlegge dagens fisketur eller en fremtidig dato?',
    aiWelcomeHistorical: 'Jeg bruker historiske værdata og prognoser for å gi deg best mulig råd.',
    aiStartQuestions: 'Begynn planlegging',
    aiQuestionIntro: 'Fortell Alfred om fisket ditt for bedre råd:',
    aiStartChat: 'Start Alfred',
    aiFishingFromShore: 'Fisker du fra land?',
    aiFishingMethod: 'Fiskemetode?',
    aiWaterClarity: 'Vannklarhet?',
    aiYes: 'Ja',
    aiNo: 'Nei',
    aiMethodSpin: 'Spinn',
    aiMethodBait: 'Agn',
    aiMethodTrolling: 'Trolling',
    aiMethodFly: 'Flue',
    aiMethodVertical: 'Vertikal',
    aiMethodLivescope: 'Livescope',
    aiClarityClear: 'Klar',
    aiClarityModerate: 'Moderat',
    aiClarityMurky: 'Grumset',
    aiSendMessage: 'Send',
    aiTypingPlaceholder: 'Skriv en melding til Alfred...',
    aiWaitingMessage: 'Hei, jeg tenker et øyeblikk...',
    aiRateLimitTitle: 'Grense nådd',
    aiRateLimitMessage: 'Du har nådd maksimalt antall AI-forespørsler (10 per time). Prøv igjen om {minutes} minutt{plural}.',
    aiRateLimitResetAt: 'Tilbakestilles kl:',
    aiErrorTitle: 'Feil',
    aiTooManyRequestsTitle: 'For mange forespørsler',
    aiTooManyRequestsMessage: 'Vennligst vent et øyeblikk før du gjør en ny forespørsel.',
    minute: 'minutt',
    minutes: 'minutter',
    
    // Wind Simulator
    windSimulator: 'Vindsimulator',
    hours: 'timer',
    days: 'dager',
    play: 'Spill',
    pause: 'Pause',
    reset: 'Tilbakestill til start',
    speed: 'Hastighet:',
    
    // Wind strength
    calm: 'Stille',
    light: 'Lett',
    moderate: 'Moderat',
    strong: 'Sterk',
    gale: 'Storm',
    
    // Wind directions
    north: 'N',
    northeast: 'NØ',
    east: 'Ø',
    southeast: 'SØ',
    south: 'S',
    southwest: 'SV',
    west: 'V',
    northwest: 'NV',
    
    // Favorites
    favorites: 'Favoritter',
    addToFavorites: 'Legg til favoritter',
    removeFromFavorites: 'Fjern fra favoritter',
    noFavorites: 'Ingen favoritter lagret ennå',
    
    // Tutorial
    tutorialSkip: 'Hopp over',
    tutorialNext: 'Neste',
    tutorialComplete: 'Fullfør',
    tutorialMapTitle: '🗺️ Interaktivt kart',
    tutorialMapDesc: 'Klikk hvor som helst på kartet for å velge en plassering og se værdata.',
    tutorialDateTitle: '📅 Velg dato',
    tutorialDateDesc: 'Velg en dato for å se historisk vær eller prognose.',
    tutorialLocationTitle: '📍 Min posisjon',
    tutorialLocationDesc: 'Trykk her for automatisk å finne din nåværende posisjon.',
    tutorialWeatherTitle: '🌤️ Værinformasjon',
    tutorialWeatherDesc: 'Se detaljert værinformasjon for valgt plassering og dato.',
    tutorialHourlyTitle: '⏰ Timeprognose',
    tutorialHourlyDesc: 'Utforsk værprognosen time for time.',
    tutorialHistoryTitle: '📈 Værhistorikk',
    tutorialHistoryDesc: 'Se værutviklingen 5 dager tilbake og fremover.',
    tutorialFishingTitle: '🎣 Fiskeprognose',
    tutorialFishingDesc: 'AI-drevet fiskeprognose basert på værdata. Velg fiskeart for skreddersydde anbefalinger.',
    tutorialAITitle: '🤖 Alfred Fiske-AI',
    tutorialAIDesc: 'Få personlige AI-anbefalinger fra Alfred basert på alle værforhold. Perfekt for både nybegynnere og erfarne fiskere!',
    tutorialWindTitle: '🌬️ Vindsimulator',
    tutorialWindDesc: 'Se animerte vindpiler som viser retning og styrke time for time. Spill av animasjonen for å se hvordan vinden endrer seg over tid.',
    tutorialFavoritesTitle: '⭐ Favoritter',
    tutorialFavoritesDesc: 'Lagre dine favorittsteder for rask tilgang.',
    
    // Toast messages
    locationAdded: 'Plassering lagt til favoritter',
    locationRemoved: 'Plassering fjernet fra favoritter',
    errorOccurred: 'En feil oppstod',
    errorFetchingWeather: 'Kunne ikke hente værdata',
    
    // Settings
    settings: 'Innstillinger',
    language: 'Språk',
    units: 'Enheter',
    defaultFishSpecies: 'Standard fiskeart',
    metric: 'Metrisk',
    imperial: 'Imperial',
    saveSettings: 'Lagre innstillinger',
    settingsUpdated: 'Innstillinger oppdatert',
    exportBackup: 'Eksporter & Backup',
    exportImportData: 'Eksporter / Importer Data',
    offlineDataSync: 'Værdata og stedsnavn synkroniseres automatisk når du går online!',
    
    // Footer
    weatherDataFrom: 'Værdata fra',
    mapsFrom: 'Kart fra',
    builtBy: 'Bygget av',
    newHere: 'Ny her?',
    showGuide: 'Vis guide',
    
    // Navigation Menu
    navigationWeather: 'Vær',
    catches: 'Fangster',
    sessions: 'Fiskeøkter',
    areas: 'Områder',
    
    // Catches Page
    noCatchesYet: 'Ingen fangster ennå',
    noCatchesDescription: 'Trykk på +-knappen for å logge din første fangst!',
    totalCatches: 'totalt',
    landed: 'landet',
    released: 'sluppet',
    photos: 'bilder',
    loadingCatches: 'Laster fangster...',
    unknownLocation: 'Ukjent plassering',
    
    // Catch Detail Page
    backToCatches: 'Tilbake til fangster',
    measurements: 'Målinger',
    waterConditions: 'Vannforhold',
    fishingDepth: 'Fiskedybde',
    outOf100: 'av 100',
    quantity: 'Antall',
    catch: 'fangst',
    notRecorded: 'Ikke registrert',
    end: 'Slutt',
    
    // Sessions Page
    noSessionsYet: 'Ingen fiskeøkter ennå',
    noSessionsDescription: 'Start en økt ved å logge en fangst!',
    sessionsTotal: 'økter totalt',
    active: 'aktive',
    completed: 'Fullført',
    fishingSession: 'Fiskeøkt',
    start: 'Start',
    duration: 'Varighet',
    waterTemp: 'Vanntemp',
    loadingSessions: 'Laster fiskeøkter...',
    
    // Areas Page
    noAreasYet: 'Ingen områder ennå',
    noAreasDescription: 'Fiskeområder opprettes automatisk når du logger fangster!',
    areasVisited: 'besøkte',
    place: 'sted',
    places: 'steder',
    coordinates: 'Koordinater',
    radius: 'Radius',
    lastVisit: 'Siste besøk',
    loadingAreas: 'Laster områder...',
    
    // Tutorial - Navigation step
    tutorialNavigationTitle: 'Navigering & Fiskelogg',
    tutorialNavigationDesc: 'Bruk menyen for å bytte mellom vær, dine fangster, fiskeøkter og områder. Trykk på +-knappen for raskt å logge en fangst!',
    
    // QuickCatchModal
    quickCatchTitle: '⚡ Hurtiglogg Fangst',
    speciesLabel: '🐟 Art',
    typeLabel: '🎯 Type',
    typeCaught: '🎣 Fanget',
    typeContact: '💨 Kontakt',
    countLabel: '🔢 Antall',
    moreDetails: 'Flere detaljer',
    weightLabel: '⚖️ Vekt',
    lengthLabel: '📏 Lengde',
    waterTempLabel: '🌡️ Vanntemperatur (°C)',
    depthLabel: '📐 Fiskedybde',
    depthPlaceholder: 'Dybde',
    photoLabel: '📸 Foto',
    takePhoto: '📷 Ta bilde',
    chooseFromGallery: '🖼️ Velg fra galleri',
    notesLabel: '📝 Notater',
    notesPlaceholder: 'Valgfrie notater...',
    advancedBacklog: '⚙️ Avansert (backlog)',
    dateLabel: '📅 Dato',
    datePlaceholder: 'La stå tom for dagens dato',
    timeLabel: '⏰ Tid',
    timePlaceholder: 'La stå tom for nåværende tid',
    sessionLabel: '🎯 Fiskeøkt',
    autoCreateSession: 'Auto-opprett ny økt',
    noSession: 'Ingen økt',
    activeSessions: '──── Aktive Økter ────',
    completedSessions: '──── Fullførte Økter ────',
    areaLabel: '📍 Område',
    selectArea: 'Velg område',
    customLocation: 'Egendefinert plassering',
    activePassSeparator: 'Aktive Økter',
    completedPassSeparator: 'Fullførte Økter',
    locationLabel: '🗺️ Plassering',
    chooseOnMap: '📍 Velg på kart',
    saveLocation: '💾 Lagre plassering',
    cancelLocation: '✕ Avbryt',
    logCatch: '✅ Logg fangst',
    logCatches: '✅ Logg fangster',
    catchLogged: 'logget',
    catchesLogged: 'logget',
    catchLogFailed: '❌ Kunne ikke logge fangst',
    cannotLogFutureDate: '❌ Kan ikke logge fangst i fremtiden',
    photoCaptured: '📷 Foto tatt!',
    photoCaptureFailed: '❌ Kunne ikke ta foto',
    photoUploaded: '🖼️ Foto lastet opp!',
    photoUploadFailed: '❌ Kunne ikke laste opp foto',
    photoRemoved: '🗑️ Foto fjernet',
    selectSpecies: 'Velg en art først',
    noArea: 'Ingen plassering',
    useGPS: 'Bruk GPS-posisjon',
    useGPSDescription: 'Henter din nåværende posisjon',
    customLocationDescription: 'Velg plassering på kart',
    noAreaDescription: 'Lagre fangst uten plasseringsinfo',
    sessionAutoCreateDescription: 'Oppretter ny økt for denne fangsten',
    sessionNoSessionDescription: 'Ingen tilkobling til økt',
    sessionActiveDescription: '🟢 = Aktiv økt, ⚪ = Fullført økt',
    
    // Catch Detail Page
    sunriseLabel: 'Soloppgang',
    sunsetLabel: 'Solnedgang',
    topCatches: 'Topp 5',
    excellent: 'Utmerket',
    good: 'God',
    fair: 'Akseptabelt',
    keepTrying: 'Fortsett å prøve',
    
    // Fishing Methods
    fishingMethod: 'Fiskemetode',
    methodSpin: 'Spinning',
    methodJigging: 'Jigging',
    methodTrolling: 'Trolling',
    methodFly: 'Fluefiske',
    methodBait: 'Agnfiske',
    methodVertical: 'Vertikal',
    methodLivescope: 'Livescope',
    methodIce: 'Isfiske',
    
    // Tackle & Bait
    tackle: 'Utstyr & Agn',
    tackleWobbler: 'Wobbler',
    tackleSoftbait: 'Gummiagn',
    tackleSpoon: 'Spinner',
    tackleJig: 'Jig',
    tackleCrankbait: 'Crankbait',
    tackleSpinnerbait: 'Spinnerbait',
    tackleTopwater: 'Topwater',
    tackleSwimbait: 'Swimbait',
    tackleDryfly: 'Tørrflue',
    tackleNymph: 'Nymfe',
    tackleStreamer: 'Streamer',
    tackleLivebait: 'Levende agn',
    tackleDeadbait: 'Dødt agn',
    tackleWorm: 'Mark',
    tackleMaggot: 'Maggot',
    tackleCorn: 'Mais',
    tackleDough: 'Deig',
    tackleDropshot: 'Dropshot-rigg',
    tackleCarolina: 'Carolina-rigg',
    tackleTexas: 'Texas-rigg',
    
    // Fishing Details UI
    selectMethod: 'Velg metode',
    selectTackle: 'Velg agn',
    recommendedMethods: 'Anbefalt',
    recentlyUsed: 'Nylig brukt',
    customTackle: 'Egendefinert agn',
    addCustomTackle: 'Legg til eget agn',
    addMethod: 'Legg til metode',
    manageMethods: 'Administrer fiskemetoder',
    manageTackle: 'Administrer agn',
    manageSpecies: 'Administrer arter',
    addSpecies: 'Legg til art',
    added: 'lagt til',
    removed: 'fjernet',
    error: 'Feil',
    defaultLabel: 'standard',
    
    // Confirmation dialogs
    confirmRemove: 'Bekreft fjerning',
    cancel: 'Avbryt',
    removeSpeciesWarning: 'Er du sikker på at du vil fjerne denne arten? Fangster med denne arten vil bli beholdt, men kan få feil fiskescore og AI-anbefalinger.',
    removeMethodWarning: 'Er du sikker på at du vil fjerne denne metoden? Fangster med denne metoden vil bli beholdt.',
    removeTackleWarning: 'Er du sikker på at du vil fjerne dette agnet? Fangster med dette agnet vil bli beholdt.',
    catchesWillRemain: 'Merk: Eksisterende fangster påvirkes ikke.',
    
    // Default preferences
    defaultSpecies: 'Standard art',
    defaultSpeciesHelp: 'Brukes til fiskescore på vær-siden og i Alfred AI når du logger fangst',
    defaultMethod: 'Standard metode',
    defaultMethodHelp: 'Velges automatisk i Alfred AI',
    defaultTackle: 'Standard agn',
    defaultTackleHelp: 'Velges automatisk i Alfred AI (kan velge flere)',
    clickToSetDefault: 'Klikk på en for å sette som standard',
    clickToSetDefaultMultiple: 'Klikk for å sette som standard (kan velge flere)',
    
    // CatchesPage Redesign - Filter & Search
    search: 'Søk',
    clearSearch: 'Fjern søk',
    searchCatches: 'Søk fangster...',
    filters: 'Filtre',
    clearFilters: 'Fjern filtre',
    applyFilters: 'Vis resultater',
    allDates: 'Alle datoer',
    thisWeek: 'Denne uken',
    thisMonth: 'Denne måneden',
    thisYear: 'Dette året',
    allAreas: 'Alle steder',
    
    // CatchesPage Redesign - Saved Filters
    allCatches: 'Alle fangster',
    createNewFilter: 'Opprett nytt filter',
    configure: 'Konfigurer',
    saveFilter: 'Lagre filter',
    updateFilter: 'Oppdater filter',
    deleteFilter: 'Slett filter',
    filterName: 'Filternavn',
    filterIcon: 'Ikon',
    noSavedFilters: 'Ingen lagrede filtre',
    
    // CatchesPage Redesign - Advanced Filters
    methods: 'Metoder',
    baits: 'Agn',
    seasons: 'Årstider',
    spring: 'Vår',
    summer: 'Sommer',
    fall: 'Høst',
    winter: 'Vinter',
    timeOfDay: 'Tid på dagen',
    morning: 'Morgen',
    afternoon: 'Ettermiddag',
    evening: 'Kveld',
    night: 'Natt',
    weatherFilters: 'Værfiltre',
    windDirections: 'Vindretninger',
    
    // CatchesPage Redesign - Sort & Group
    groupBy: 'Gruppér etter',
    sortBy: 'Sortér etter',
    date: 'Dato',
    area: 'Sted',
    session: 'Økt',
    none: 'Ingen',
    newest: 'Nyeste',
    oldest: 'Eldste',
    weight: 'Vekt',
    length: 'Lengde',
    
    // CatchesPage Redesign - View modes
    view: 'Visning',
    list: 'Liste',
    grid: 'Rutenett',
    map: 'Kart',
    
    // CatchesPage Redesign - Selection & Bulk
    selectMultiple: 'Velg flere',
    selected: 'valgte',
    delete: 'Slett',
    export: 'Eksportér',
    changeArea: 'Endre sted',
    addToSession: 'Legg til i økt',
    
    // CatchesPage Redesign - Stats
    contact: 'kontakt',
    total: 'totalt',
    
    // CatchesPage Redesign - Map
    showMarkers: 'Vis markører',
    showHeatmap: 'Vis heatmap',
    viewDetails: 'Se detaljer',
    
    // CatchesPage Redesign - Weather Similarity
    findSimilarWeather: 'Finn lignende vær',
    similarConditions: 'Lignende forhold',
    referenceWeather: 'Referansevær',
    foundSimilar: 'Fant {count} fangster med lignende vær',
    matchScore: 'Match',
    viewAllOnMap: 'Se alle på kart',
    exportSimilar: 'Eksportér lignende',
    
    // CatchesPage Redesign - Empty states
    noMatchingCatches: 'Ingen fangster matcher filtrene',
    clearFiltersToSeeMore: 'Fjern filtre for å se flere fangster',
    
    // CatchesPage Redesign - Loading
    loadMore: 'Last flere',
  },
  
  fi: {
    // Header
    appTitle: 'Kalastus & Sää',
    serviceFrom: 'Palvelu: skitfiske.app',
    contactUs: 'Ota yhteyttä',
    
    // Share
    shareWeather: 'Jaa sää',
    linkCopied: 'Linkki kopioitu leikepöydälle!',
    weatherAppTitle: 'SääSovellus - Sääennuste',
    checkWeatherFor: 'Tarkista sää kohteelle',
    
    // Welcome Screen
    welcomeTitle: 'Tervetuloa Kalastus & Sää -palveluun',
    welcomeSubtitle: 'Älykäs kalastusopas AI-analyysillä',
    welcomeButton: 'Aloita',
    
    // Map & Controls
    selectLocation: 'Valitse sijainti kartalta',
    clickToExplore: 'Tutkia',
    currentLocationBtn: 'Oma sijainti',
    locationError: 'Sijaintia ei voitu hakea',
    
    // Date
    selectDate: 'Valitse päivämäärä',
    time: 'Aika',
    
    // Weather Display
    loading: 'Ladataan...',
    weather: 'Sää',
    noWeatherData: 'Ei saatavilla olevaa säätietoa',
    temperature: 'Lämpötila',
    feelsLike: 'Tuntuu kuin',
    humidity: 'Ilmankosteus',
    pressure: 'Ilmanpaine',
    wind: 'Tuuli',
    windGust: 'Puuskat',
    precipitation: 'Sademäärä',
    visibility: 'Näkyvyys',
    cloudiness: 'Pilvisyys',
    sunrise: 'Auringonnousu',
    sunset: 'Auringonlasku',
    moonPhase: 'Kuun vaihe',
    sunAndMoon: 'Aurinko & Kuu',
    
    // Weather Conditions
    weatherClear: 'Selkeää',
    weatherMostlyClear: 'Enimmäkseen selkeää',
    weatherPartlyCloudy: 'Puolipilvistä',
    weatherCloudy: 'Pilvistä',
    weatherFog: 'Sumua',
    weatherRimeFog: 'Huurresumua',
    weatherLightDrizzle: 'Kevyttä tihkusadetta',
    weatherModerateDrizzle: 'Kohtalaista tihkusadetta',
    weatherDenseDrizzle: 'Tiheää tihkusadetta',
    weatherLightRain: 'Kevyttä sadetta',
    weatherModerateRain: 'Kohtalaista sadetta',
    weatherHeavyRain: 'Voimakasta sadetta',
    weatherLightSnow: 'Kevyttä lumisadetta',
    weatherModerateSnow: 'Kohtalaista lumisadetta',
    weatherHeavySnow: 'Voimakasta lumisadetta',
    weatherLightShowers: 'Kevyitä kuuroja',
    weatherModerateShowers: 'Kohtalaisia kuuroja',
    weatherHeavyShowers: 'Voimakkaita kuuroja',
    weatherThunderstorm: 'Ukkosmyrsky',
    weatherThunderstormHail: 'Ukkosmyrsky ja rakeita',
    weatherUnknown: 'Tuntematon',
    historicalWeather: 'Historiallinen sää',
    currentWeather: 'Nykyinen sää',
    forecast: 'Ennuste',
    
    // Moon Phases
    newMoon: 'Uusikuu',
    waxingCrescent: 'Kasvava sirppi',
    firstQuarter: 'Ensimmäinen neljännes',
    waxingGibbous: 'Kasvava kuu',
    fullMoon: 'Täysikuu',
    waningGibbous: 'Vähenevä kuu',
    lastQuarter: 'Viimeinen neljännes',
    waningCrescent: 'Vähenevä sirppi',
    
    // Hourly Weather
    hourlyForecast: 'Tuntikohtainen ennuste',
    hourlyForecastShort: '24h ennuste',
    hour: 'Tunti',
    
    // Weather History
    weatherHistory: 'Säähistoria',
    daysBackAndForward: '5 päivää taaksepäin & eteenpäin alkaen',
    daysBackAndForward2: '5 päivää taaksepäin & eteenpäin alkaen {date}',
    historicalWeatherDevelopment: 'Historiallinen sääkehitys valitulle paikalle',
    astronomy: 'Tähtitiede',
    maxTemp: 'Max lämpötila',
    minTemp: 'Min lämpötila',
    windSpeed: 'Tuulen nopeus',
    windDirection: 'Tuulen suunta',
    windGustLabel: 'Tuulenpuuskat',
    timeHours: 'Aika (tunnit)',
    moonPhasePercent: 'Kuun vaihe (%)',
    
    // Weather Trends - Insights
    tempChange: 'Lämpötilan muutos',
    pressureChange: 'Paineen muutos',
    minPressure: 'Min paine',
    maxPressure: 'Max paine',
    avgWind: 'Keskim. tuuli',
    maxGust: 'Max puuska',
    dominantDirection: 'Hallitseva suunta',
    windSpan: 'Tuulen vaihteluväli',
    totalPrecipitation: 'Kokonaissademäärä',
    maxPerDay: 'Max/päivä',
    rainyDays: 'Sadepäiviä',
    avgScore: 'Keskim. pisteet',
    maxScore: 'Max pisteet',
    quality: 'Laatu',
    catchDay: 'SAALISTUSPÄIVÄ',
    catchMomentLegend: 'Vihreä piste = Saalistushetki',
    
    
    // Fishing Forecast
    fishingForecast: 'Kalastusennuste',
    advancedAlgorithm: 'Edistynyt algoritmi perustuen säätietoihin & kuun vaiheisiin',
    fishingScore: 'Kalastuspisteet',
    today: 'tänään',
    excellentFishing: 'Erinomainen kalastussää!',
    goodConditions: 'Hyvät olosuhteet',
    moderateConditions: 'Kohtalaiset olosuhteet',
    difficultConditions: 'Vaikeat olosuhteet',
    positiveFactors: 'Positiiviset:',
    negativeFactors: 'Negatiiviset:',
    hourByHour: 'Tunti tunnilta',
    upcomingDays: '5 päivää taaksepäin & eteenpäin alkaen {date}',
    species: 'Laji:',
    
    // Fishing factors
    excellentLight: 'Erinomaiset valoolosuhteet',
    goodLight: 'Hyvä valo kalastukseen',
    moderateLight: 'Kohtalaiset valoolosuhteet',
    poorLight: 'Huonot valoolosuhteet',
    stableHighPressure: 'Vakaa korkea ilmanpaine suosii',
    strongWeatherFront: 'Voimakas säärintama!',
    strongPressureFront: 'Voimakas ilmanpainerintama',
    pressureFront: 'Ilmanpainerintama',
    niceWind: 'Hyvä tuuli',
    extremeConditions: 'Ääriolosuhteet!',
    tooHard: 'Liian kovaa!',
    dangerousGusts: 'Vaarallisen puuskaiset!',
    poorWindConditions: 'Huonot tuuliolosuhteet',
    stronglyGusty: 'Voimakkaasti puuskainen',
    hardGusts: 'Kovät puuskat',
    problematicWind: 'Ongelmallinen tuuli',
    excellentWeather: 'Erinomaiset sääolosuhteet',
    poorWeatherFront: 'Huono sää: ilmanpainerintama & kova tuuli',
    poorWeatherWind: 'Huono sää: liian kova tuuli',
    poorWeatherRain: 'Huono sää: sade/huono näkyvyys',
    poorWeather: 'Huonot sääolosuhteet',
    acceptableWeather: 'Hyväksyttävät sääolosuhteet',
    goodWaterTemp: 'Hyvä veden lämpötila',
    unfavorableWaterTemp: 'Epäsuotuisa veden lämpötila',
    moderateWaterTemp: 'Kohtalainen veden lämpötila',
    weatherFrontAffects: 'Säärintama vaikuttaa',
    veryStableWeather: 'Erittäin vakaa sää',
    unstableWeather: 'Epävakaa sää',
    extremelyWindy: 'Äärimmäisen tuulinen - VAARALLINEN',
    veryWindy: 'Erittäin tuulinen',
    hardWindy: 'Kovasti tuulinen',
    dangerousGustsWarning: 'Vaaralliset puuskat',
    extremeRain: 'Äärimmäisen voimakas sade',
    veryHeavyRain: 'Erittäin voimakas sade',
    extremelyPoorVisibility: 'Äärimmäisen huono näkyvyys - VAARALLINEN',
    veryPoorVisibility: 'Erittäin huono näkyvyys',
    
    // Species names
    pike: 'Hauki',
    perch: 'Ahven',
    pikeperch: 'Kuha',
    zander: 'Kuha', // deprecated
    general: 'Yleinen',
    trout: 'Taimen',
    salmon: 'Lohi',
    seaTrout: 'Meritaimen',
    roach: 'Särki',
    bream: 'Lahna',
    carp: 'Karppi',
    tench: 'Suutari',
    ruffe: 'Kiiski',
    crucian: 'Ruutana',
    other: 'Muu',
    
    // AI Recommendation
    alfredAI: 'Alfred AI Kalastusopas',
    aiAnalyzing: 'Alfred analysoi...',
    aiWaitTime: 'Tämä voi kestää jopa 30 sekuntia ⏱️',
    aiCacheInfo: 'Keskustelu tallennettu per sijainti, päivämäärä, kieli & laji • Sis. säähistoria & kuun vaihe',
    aiWelcomeTitle: 'Hei, nimeni on Alfred! 👋',
    aiWelcomeIntro: 'Voin auttaa sinua suunnittelemaan kalastuspäivän',
    aiWelcomePastDate: 'Haluatko mieluummin suunnitella tämän päivän vai tulevan päivän kalastusretken?',
    aiWelcomeHistorical: 'Käytän historiallisia säätietoja ja ennusteita antaakseni sinulle parhaat mahdolliset neuvot.',
    aiStartQuestions: 'Aloita suunnittelu',
    aiQuestionIntro: 'Kerro Alfredille kalastuksestasi parempia neuvoja varten:',
    aiStartChat: 'Käynnistä Alfred',
    aiFishingFromShore: 'Kalastatko rannalta?',
    aiFishingMethod: 'Kalastusmenetelmä?',
    aiWaterClarity: 'Veden kirkkaus?',
    aiYes: 'Kyllä',
    aiNo: 'Ei',
    aiMethodSpin: 'Heitto',
    aiMethodBait: 'Syötti',
    aiMethodTrolling: 'Uistelu',
    aiMethodFly: 'Perho',
    aiMethodVertical: 'Vertikaalikalastus',
    aiMethodLivescope: 'Livescope',
    aiClarityClear: 'Kirkas',
    aiClarityModerate: 'Kohtalainen',
    aiClarityMurky: 'Samea',
    aiSendMessage: 'Lähetä',
    aiTypingPlaceholder: 'Kirjoita viesti Alfredille...',
    aiWaitingMessage: 'Hei, ajattelen hetken...',
    aiRateLimitTitle: 'Raja saavutettu',
    aiRateLimitMessage: 'Olet saavuttanut AI-pyyntöjen enimmäismäärän (10 tunnissa). Yritä uudelleen {minutes} minuutin kuluttua.',
    aiRateLimitResetAt: 'Nollautuu klo:',
    aiErrorTitle: 'Virhe',
    aiTooManyRequestsTitle: 'Liikaa pyyntöjä',
    aiTooManyRequestsMessage: 'Odota hetki ennen uuden pyynnön tekemistä.',
    minute: 'minuutti',
    minutes: 'minuuttia',
    
    // Wind Simulator
    windSimulator: 'Tuulisimulaattori',
    hours: 'tuntia',
    days: 'päivää',
    play: 'Toista',
    pause: 'Tauko',
    reset: 'Nollaa alkuun',
    speed: 'Nopeus:',
    
    // Wind strength
    calm: 'Tyyntä',
    light: 'Heikkoa',
    moderate: 'Kohtalaista',
    strong: 'Voimakasta',
    gale: 'Myrsky',
    
    // Wind directions
    north: 'P',
    northeast: 'KA',
    east: 'I',
    southeast: 'KE',
    south: 'E',
    southwest: 'LO',
    west: 'L',
    northwest: 'LU',
    
    // Favorites
    favorites: 'Suosikit',
    addToFavorites: 'Lisää suosikkeihin',
    removeFromFavorites: 'Poista suosikeista',
    noFavorites: 'Ei vielä tallennettuja suosikkeja',
    
    // Tutorial
    tutorialSkip: 'Ohita',
    tutorialNext: 'Seuraava',
    tutorialComplete: 'Valmis',
    tutorialMapTitle: '🗺️ Interaktiivinen kartta',
    tutorialMapDesc: 'Klikkaa missä tahansa kartalla valitaksesi sijainnin ja nähdäksesi säätiedot.',
    tutorialDateTitle: '📅 Valitse päivämäärä',
    tutorialDateDesc: 'Valitse päivämäärä nähdäksesi historiallisen sään tai ennusteen.',
    tutorialLocationTitle: '📍 Oma sijainti',
    tutorialLocationDesc: 'Paina tästä löytääksesi automaattisesti nykyisen sijaintisi.',
    tutorialWeatherTitle: '🌤️ Säätiedot',
    tutorialWeatherDesc: 'Katso yksityiskohtaiset säätiedot valitulle sijainnille ja päivälle.',
    tutorialHourlyTitle: '⏰ Tuntikohtainen ennuste',
    tutorialHourlyDesc: 'Tutki sääennustetta tunti tunnilta.',
    tutorialHistoryTitle: '📈 Säähistoria',
    tutorialHistoryDesc: 'Katso sään kehitys 5 päivää taaksepäin ja eteenpäin.',
    tutorialFishingTitle: '🎣 Kalastusennuste',
    tutorialFishingDesc: 'AI-pohjainen kalastusennuste säätietojen perusteella. Valitse kalalaji räätälöityä suositusta varten.',
    tutorialAITitle: '🤖 Alfred Kalastus-AI',
    tutorialAIDesc: 'Saa henkilökohtaiset AI-suositukset Alfredilta kaikkien sääolosuhteiden perusteella. Täydellinen sekä aloittelijoille että kokeneille kalastajille!',
    tutorialWindTitle: '🌬️ Tuulisimulaattori',
    tutorialWindDesc: 'Katso animoidut tuulenuolet, jotka näyttävät suunnan ja voimakkuuden tunti tunnilta. Toista animaatio nähdäksesi kuinka tuuli muuttuu ajan myötä.',
    tutorialFavoritesTitle: '⭐ Suosikit',
    tutorialFavoritesDesc: 'Tallenna suosikkisijaintisi nopeaa pääsyä varten.',
    
    // Toast messages
    locationAdded: 'Sijainti lisätty suosikkeihin',
    locationRemoved: 'Sijainti poistettu suosikeista',
    errorOccurred: 'Tapahtui virhe',
    errorFetchingWeather: 'Säätietoja ei voitu hakea',
    
    // Settings
    settings: 'Asetukset',
    language: 'Kieli',
    units: 'Yksiköt',
    defaultFishSpecies: 'Oletuslaji',
    metric: 'Metrinen',
    imperial: 'Brittiläinen',
    saveSettings: 'Tallenna asetukset',
    settingsUpdated: 'Asetukset päivitetty',
    exportBackup: 'Vie & Varmuuskopio',
    exportImportData: 'Vie / Tuo Dataa',
    offlineDataSync: 'Säätiedot ja paikannimen synkronoituvat automaattisesti, kun menet online-tilaan!',
    
    // Footer
    weatherDataFrom: 'Säädata',
    mapsFrom: 'Kartat',
    builtBy: 'Tehnyt',
    newHere: 'Uusi täällä?',
    showGuide: 'Näytä opas',
    
    // Navigation Menu
    navigationWeather: 'Sää',
    catches: 'Saaliit',
    sessions: 'Kalastusistunnot',
    areas: 'Alueet',
    
    // Catches Page
    noCatchesYet: 'Ei vielä saaliita',
    noCatchesDescription: 'Paina +-painiketta kirjataksesi ensimmäinen saaliisi!',
    totalCatches: 'yhteensä',
    landed: 'saatu',
    released: 'vapautettu',
    photos: 'kuvat',
    loadingCatches: 'Ladataan saaliita...',
    unknownLocation: 'Tuntematon sijainti',
    
    // Catch Detail Page
    backToCatches: 'Takaisin saaliisiin',
    measurements: 'Mittaukset',
    waterConditions: 'Vesiolosuhteet',
    fishingDepth: 'Kalastussyvyys',
    outOf100: '/ 100',
    quantity: 'Määrä',
    catch: 'saalis',
    notRecorded: 'Ei kirjattu',
    end: 'Loppu',
    
    // Sessions Page
    noSessionsYet: 'Ei vielä kalastusistuntoja',
    noSessionsDescription: 'Aloita istunto kirjaamalla saalis!',
    sessionsTotal: 'istuntoa yhteensä',
    active: 'aktiivisia',
    completed: 'Valmis',
    fishingSession: 'Kalastusistunto',
    start: 'Alku',
    duration: 'Kesto',
    waterTemp: 'Veden lämpötila',
    loadingSessions: 'Ladataan kalastusistuntoja...',
    
    // Areas Page
    noAreasYet: 'Ei vielä alueita',
    noAreasDescription: 'Kalastusalueet luodaan automaattisesti, kun kirjaat saaliita!',
    areasVisited: 'käyty',
    place: 'paikka',
    places: 'paikkaa',
    coordinates: 'Koordinaatit',
    radius: 'Säde',
    lastVisit: 'Viimeisin käynti',
    loadingAreas: 'Ladataan alueita...',
    
    // Tutorial - Navigation step
    tutorialNavigationTitle: 'Navigointi & Kalastuspäiväkirja',
    tutorialNavigationDesc: 'Käytä valikkoa vaihtaaksesi sään, saaliiden, kalastusistuntojen ja alueiden välillä. Paina +-painiketta kirjataksesi saaliin nopeasti!',
    
    // QuickCatchModal
    quickCatchTitle: '⚡ Pikaloki Saalis',
    speciesLabel: '🐟 Laji',
    typeLabel: '🎯 Tyyppi',
    typeCaught: '🎣 Saatu',
    typeContact: '💨 Kontakti',
    countLabel: '🔢 Määrä',
    moreDetails: 'Lisää yksityiskohtia',
    weightLabel: '⚖️ Paino',
    lengthLabel: '📏 Pituus',
    waterTempLabel: '🌡️ Veden lämpötila (°C)',
    depthLabel: '📐 Kalastussyvyys',
    depthPlaceholder: 'Syvyys',
    photoLabel: '📸 Kuva',
    takePhoto: '📷 Ota kuva',
    chooseFromGallery: '🖼️ Valitse galleriasta',
    notesLabel: '📝 Muistiinpanot',
    notesPlaceholder: 'Vapaamuotoiset muistiinpanot...',
    advancedBacklog: '⚙️ Edistynyt (backlog)',
    dateLabel: '📅 Päivämäärä',
    datePlaceholder: 'Jätä tyhjäksi tämän päivän osalta',
    timeLabel: '⏰ Aika',
    timePlaceholder: 'Jätä tyhjäksi nykyiselle ajalle',
    sessionLabel: '🎯 Kalastusistunto',
    autoCreateSession: 'Luo uusi istunto automaattisesti',
    noSession: 'Ei istuntoa',
    activeSessions: '──── Aktiiviset Istunnot ────',
    completedSessions: '──── Valmiit Istunnot ────',
    areaLabel: '📍 Alue',
    selectArea: 'Valitse alue',
    customLocation: 'Oma sijainti',
    activePassSeparator: 'Aktiiviset Istunnot',
    completedPassSeparator: 'Valmiit Istunnot',
    locationLabel: '🗺️ Sijainti',
    chooseOnMap: '📍 Valitse kartalla',
    saveLocation: '💾 Tallenna sijainti',
    cancelLocation: '✕ Peruuta',
    logCatch: '✅ Kirjaa saalis',
    logCatches: '✅ Kirjaa saaliit',
    catchLogged: 'kirjattu',
    catchesLogged: 'kirjattu',
    catchLogFailed: '❌ Saaliin kirjaus epäonnistui',
    cannotLogFutureDate: '❌ Ei voi kirjata saalista tulevaisuudessa',
    photoCaptured: '📷 Kuva otettu!',
    photoCaptureFailed: '❌ Kuvaa ei voitu ottaa',
    photoUploaded: '🖼️ Kuva ladattu!',
    photoUploadFailed: '❌ Kuvaa ei voitu ladata',
    photoRemoved: '🗑️ Kuva poistettu',
    selectSpecies: 'Valitse laji ensin',
    noArea: 'Ei sijaintia',
    useGPS: 'Käytä GPS-sijaintia',
    useGPSDescription: 'Hakee nykyisen sijaintisi',
    customLocationDescription: 'Valitse sijainti kartalla',
    noAreaDescription: 'Tallenna saalis ilman sijaintitietoja',
    sessionAutoCreateDescription: 'Luo uusi istunto tälle saaliille',
    sessionNoSessionDescription: 'Ei yhteyttä istuntoon',
    sessionActiveDescription: '🟢 = Aktiivinen istunto, ⚪ = Valmis istunto',
    
    // Catch Detail Page
    sunriseLabel: 'Auringonnousu',
    sunsetLabel: 'Auringonlasku',
    topCatches: 'Top 5',
    excellent: 'Erinomainen',
    good: 'Hyvä',
    fair: 'Hyväksyttävä',
    keepTrying: 'Jatka yrittämistä',
    
    // Fishing Methods
    fishingMethod: 'Kalastusmenetelmä',
    methodSpin: 'Perhokalastus',
    methodJigging: 'Jiggaus',
    methodTrolling: 'Uistelu',
    methodFly: 'Perhokalastus',
    methodBait: 'Syöttikalastus',
    methodVertical: 'Vertikaalikalastus',
    methodLivescope: 'Livescope',
    methodIce: 'Pilkkiminen',
    
    // Tackle & Bait
    tackle: 'Välineet & Syötit',
    tackleWobbler: 'Vaappu',
    tackleSoftbait: 'Kumisyötti',
    tackleSpoon: 'Lusikka',
    tackleJig: 'Jigi',
    tackleCrankbait: 'Crankbait',
    tackleSpinnerbait: 'Spinnerbait',
    tackleTopwater: 'Pintasyötti',
    tackleSwimbait: 'Swimbait',
    tackleDryfly: 'Kuivaperho',
    tackleNymph: 'Nymfi',
    tackleStreamer: 'Streamer',
    tackleLivebait: 'Elävä syötti',
    tackleDeadbait: 'Kuollut syötti',
    tackleWorm: 'Mato',
    tackleMaggot: 'Mato',
    tackleCorn: 'Maissi',
    tackleDough: 'Taikina',
    tackleDropshot: 'Dropshot-vapa',
    tackleCarolina: 'Carolina-vapa',
    tackleTexas: 'Texas-vapa',
    
    // Fishing Details UI
    selectMethod: 'Valitse menetelmä',
    selectTackle: 'Valitse syötit',
    recommendedMethods: 'Suositeltu',
    recentlyUsed: 'Äskettäin käytetty',
    customTackle: 'Omat syötit',
    addCustomTackle: 'Lisää oma syötti',
    addMethod: 'Lisää menetelmä',
    manageMethods: 'Hallitse kalastusmenetelmiä',
    manageTackle: 'Hallitse syöttejä',
    manageSpecies: 'Hallitse lajeja',
    addSpecies: 'Lisää laji',
    added: 'lisätty',
    removed: 'poistettu',
    error: 'Virhe',
    defaultLabel: 'oletus',
    
    // Confirmation dialogs
    confirmRemove: 'Vahvista poisto',
    cancel: 'Peruuta',
    removeSpeciesWarning: 'Oletko varma, että haluat poistaa tämän lajin? Tämän lajin saaliit säilytetään, mutta niiden kalapisteistä ja tekoälysuosituksista voi tulla virheellisiä.',
    removeMethodWarning: 'Oletko varma, että haluat poistaa tämän menetelmän? Tämän menetelmän saaliit säilytetään.',
    removeTackleWarning: 'Oletko varma, että haluat poistaa tämän syötin? Tämän syötin saaliit säilytetään.',
    catchesWillRemain: 'Huomaa: Olemassa olevat saaliit eivät muutu.',
    
    // Default preferences
    defaultSpecies: 'Oletuslaji',
    defaultSpeciesHelp: 'Käytetään kalapisteisiin sää-sivulla ja Alfred AI:ssa kun kirjaat saaliin',
    defaultMethod: 'Oletusmenetelmä',
    defaultMethodHelp: 'Valitaan automaattisesti Alfred AI:ssa',
    defaultTackle: 'Oletussyötit',
    defaultTackleHelp: 'Valitaan automaattisesti Alfred AI:ssa (voi valita useita)',
    clickToSetDefault: 'Klikkaa yhtä asettaaksesi oletukseksi',
    clickToSetDefaultMultiple: 'Klikkaa asettaaksesi oletukseksi (voit valita useita)',
    
    // CatchesPage Redesign - Filter & Search
    search: 'Etsi',
    clearSearch: 'Tyhjennä haku',
    searchCatches: 'Etsi saaliita...',
    filters: 'Suodattimet',
    clearFilters: 'Tyhjennä suodattimet',
    applyFilters: 'Näytä tulokset',
    allDates: 'Kaikki päivät',
    thisWeek: 'Tämä viikko',
    thisMonth: 'Tämä kuukausi',
    thisYear: 'Tämä vuosi',
    allAreas: 'Kaikki paikat',
    
    // CatchesPage Redesign - Saved Filters
    allCatches: 'Kaikki saaliit',
    createNewFilter: 'Luo uusi suodatin',
    configure: 'Määritä',
    saveFilter: 'Tallenna suodatin',
    updateFilter: 'Päivitä suodatin',
    deleteFilter: 'Poista suodatin',
    filterName: 'Suodattimen nimi',
    filterIcon: 'Kuvake',
    noSavedFilters: 'Ei tallennettuja suodattimia',
    
    // CatchesPage Redesign - Advanced Filters
    methods: 'Menetelmät',
    baits: 'Syötit',
    seasons: 'Vuodenajat',
    spring: 'Kevät',
    summer: 'Kesä',
    fall: 'Syksy',
    winter: 'Talvi',
    timeOfDay: 'Vuorokaudenaika',
    morning: 'Aamu',
    afternoon: 'Iltapäivä',
    evening: 'Ilta',
    night: 'Yö',
    weatherFilters: 'Sääsuodattimet',
    windDirections: 'Tuulensuunnat',
    
    // CatchesPage Redesign - Sort & Group
    groupBy: 'Ryhmittele',
    sortBy: 'Järjestä',
    date: 'Päivä',
    area: 'Paikka',
    session: 'Sessio',
    none: 'Ei mitään',
    newest: 'Uusin',
    oldest: 'Vanhin',
    weight: 'Paino',
    length: 'Pituus',
    
    // CatchesPage Redesign - View modes
    view: 'Näkymä',
    list: 'Lista',
    grid: 'Ruudukko',
    map: 'Kartta',
    
    // CatchesPage Redesign - Selection & Bulk
    selectMultiple: 'Valitse useita',
    selected: 'valittua',
    delete: 'Poista',
    export: 'Vie',
    changeArea: 'Vaihda paikkaa',
    addToSession: 'Lisää sessioon',
    
    // CatchesPage Redesign - Stats
    contact: 'kontakti',
    total: 'yhteensä',
    
    // CatchesPage Redesign - Map
    showMarkers: 'Näytä merkinnät',
    showHeatmap: 'Näytä lämpökartta',
    viewDetails: 'Näytä tiedot',
    
    // CatchesPage Redesign - Weather Similarity
    findSimilarWeather: 'Etsi samanlaista säätä',
    similarConditions: 'Samanlaiset olosuhteet',
    referenceWeather: 'Viitesää',
    foundSimilar: 'Löytyi {count} saalista samanlaisella säällä',
    matchScore: 'Osuma',
    viewAllOnMap: 'Näytä kaikki kartalla',
    exportSimilar: 'Vie samanlaiset',
    
    // CatchesPage Redesign - Empty states
    noMatchingCatches: 'Ei saaliita suodattimilla',
    clearFiltersToSeeMore: 'Tyhjennä suodattimet nähdäksesi lisää',
    
    // CatchesPage Redesign - Loading
    loadMore: 'Lataa lisää',
  },
};

export function getTranslation(lang: Language): Translations {
  return translations[lang] || translations.sv;
}
